import React, { useState, useEffect } from 'react';
import { LegalModal, PrivacyPolicyContent, TermsOfServiceContent } from './LegalModals';
import { StripeMockCheckoutModal, StripeCheckoutPlan } from './StripeMockCheckoutModal';
import { LuminousBreakLogo } from './LuminousBreakLogo';
import { StripeCustomerPortalModal } from './StripeCustomerPortalModal';

export interface MarketingLandingPageProps {
  onOpenPortal: () => void;
  onOpenBillingPortal?: () => void;
  onNavigateRoute?: (route: 'privacy' | 'terms' | 'support') => void;
}

type DemoCategory = 'financial' | 'media' | 'spending' | 'eating' | 'substance' | 'screen' | 'adult' | 'gaming' | 'work';

interface DemoScenario {
  id: DemoCategory;
  title: string;
  icon: string;
  triggerApp: string;
  prompt: string;
  technique: string;
}

const DEMO_SCENARIOS: Record<DemoCategory, DemoScenario> = {
  financial: {
    id: 'financial',
    title: 'Financial & Trading',
    icon: '📈',
    triggerApp: 'Robinhood / Crypto Exchange',
    prompt: 'Notice the physical surge of urgency in your chest. Is this trade aligned with your long-term plan, or an emotional impulse?',
    technique: 'Urge Surfing & Loss Curve Perspective',
  },
  media: {
    id: 'media',
    title: 'Mindful Social Media',
    icon: '📱',
    triggerApp: 'TikTok / Infinite Feed',
    prompt: 'Are you opening this feed to find specific inspiration, or to escape an uncomfortable feeling right now?',
    technique: '3-Sentence CBT Thought Reframing',
  },
  spending: {
    id: 'spending',
    title: 'Impulse Shopping',
    icon: '🛍️',
    triggerApp: 'Amazon / Checkout Cart',
    prompt: 'This purchase equals 3.5 hours of your hard work. Take a 2-minute cooling-off breath before completing checkout.',
    technique: 'Wage-to-Hour Values Alignment',
  },
  eating: {
    id: 'eating',
    title: 'Late-Night Food Delivery',
    icon: '🥗',
    triggerApp: 'DoorDash / Delivery App',
    prompt: 'Drink a glass of water and pause for 3 deep breaths. Is your body genuinely hungry, or seeking comfort?',
    technique: 'Somatic Sensory Grounding',
  },
  substance: {
    id: 'substance',
    title: 'Substance & Alcohol',
    icon: '🍷',
    triggerApp: 'Delivery & Liquor App',
    prompt: 'Remember your commitment to tomorrow morning’s energy and vitality. Listen to your recorded supportive voice note.',
    technique: 'Supportive Voice Note Intercept',
  },
  screen: {
    id: 'screen',
    title: 'Screen Time & Sleep',
    icon: '⏳',
    triggerApp: 'Late-Night Browser (11 PM)',
    prompt: 'It is 11:30 PM. Your brain needs restorative rest. Roll your shoulders, set your alarm, and put your phone down.',
    technique: '1-Minute Physical Somatic Stretch',
  },
  adult: {
    id: 'adult',
    title: 'Adult Media Guardrails',
    icon: '🔞',
    triggerApp: 'Explicit / Streaming Platform',
    prompt: 'Step back and observe the dopamine craving cycle. What emotional need (loneliness, boredom, stress) is seeking comfort?',
    technique: 'Mindful Urge Acceptance & Redirection',
  },
  gaming: {
    id: 'gaming',
    title: 'Gaming & In-Game Loot',
    icon: '🎮',
    triggerApp: 'Steam / Roblox Store',
    prompt: 'This microtransaction gives a momentary reward. Will this purchase matter to your goals 30 days from today?',
    technique: 'Delayed Gratification Lock',
  },
  work: {
    id: 'work',
    title: 'After-Hours Work Boundaries',
    icon: '💼',
    triggerApp: 'Slack / Work Email (8:30 PM)',
    prompt: 'You are off the clock. Protect your evening peace and recovery. This message can safely wait until tomorrow morning.',
    technique: 'Boundary Reaffirmation & Rest Reset',
  },
};

const DEMO_TIER_CONFIGS: Record<1 | 2 | 3 | 4 | 5, { name: string; shortLabel: string; info: string; color: string; bg: string; border: string }> = {
  1: {
    name: 'Tier 1: Soft Pause (10s)',
    shortLabel: 'Tier 1: Soft Pause (10s)',
    info: 'Tier 1: Soft Pause (10s) • Mandatory micro-pause allowing prefrontal cortex to interrupt dopamine loops before interaction.',
    color: '#26594A',
    bg: '#EBF3F0',
    border: '#749C8F',
  },
  2: {
    name: 'Tier 2: Reflective Journal (90s)',
    shortLabel: 'Tier 2: Reflective Journal (90s)',
    info: 'Tier 2: Reflective Journal (90s) • Dialectical CBT inquiry identifying cognitive distortions and automatic thought reframing.',
    color: '#8A5D00',
    bg: '#FFF8E7',
    border: '#E0A92C',
  },
  3: {
    name: 'Tier 3: Somatic Grounding (3m)',
    shortLabel: 'Tier 3: Somatic Grounding (3m)',
    info: 'Tier 3: Somatic Grounding (3m) • Parasympathetic vagal activation through rhythmic breathing to downregulate acute autonomic arousal.',
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#3B82F6',
  },
  4: {
    name: 'Tier 4: Cooling Contract (120s)',
    shortLabel: 'Tier 4: Cooling Contract (120s)',
    info: 'Tier 4: Cooling Contract (120s) • Enforces 120s time-lock & values commitment to overcome impulsive autopilot momentum.',
    color: '#9E3C22',
    bg: '#FFF3EC',
    border: '#E07A5F',
  },
  5: {
    name: 'Tier 5: Hard Lock (Sleep Curfew)',
    shortLabel: 'Tier 5: Hard Lock (Sleep Curfew)',
    info: 'Tier 5: Hard Lock (Sleep Curfew) • Absolute zero-bypass blocking shield during vulnerable overnight hours (10:00 PM – 6:00 AM).',
    color: '#991B1B',
    bg: '#FEF2F2',
    border: '#EF4444',
  },
};

const DEMO_BLOCK_TABS: Array<{ id: 'somatic' | 'cognitive' | 'friction' | 'support'; label: string; icon: string }> = [
  { id: 'somatic', label: '🌊 Somatic Pacer', icon: '🌊' },
  { id: 'cognitive', label: '🧠 Cognitive Reframe', icon: '🧠' },
  { id: 'friction', label: '⏳ Intentional Friction', icon: '⏳' },
  { id: 'support', label: '🎙️ Support & 988', icon: '🎙️' },
];

const DEMO_CORE_VALUES: string[] = [
  'Restorative Sleep',
  'Financial Freedom',
  'Family & Loved Ones',
  'Mental Clarity',
  'Physical Health',
];

const DEMO_DISTORTION_TAGS: string[] = [
  'Catastrophizing',
  'Emotional Reasoning',
  'FOMO Urgency',
  'Dopamine Loop',
];

export const MarketingLandingPage: React.FC<MarketingLandingPageProps> = ({
  onOpenPortal,
  onOpenBillingPortal,
  onNavigateRoute,
}) => {
  const [activeCategory, setActiveCategory] = useState<DemoCategory>('financial');
  // On-demand interactive simulator: demoActive starts false to prevent autoplay & background timers
  const [demoActive, setDemoActive] = useState<boolean>(false);
  const [breathPhase, setBreathPhase] = useState<'Inhale' | 'Hold' | 'Exhale'>('Inhale');
  const [breathSeconds, setBreathSeconds] = useState<number>(4);
  const [urgeRating, setUrgeRating] = useState<number>(8);
  const [postUrgeRating, setPostUrgeRating] = useState<number>(3);
  const [demoCompleted, setDemoCompleted] = useState<boolean>(false);

  // Strictness Tier Escalation & Multi-Block Simulator State
  const [demoActiveTier, setDemoActiveTier] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [demoActiveBlock, setDemoActiveBlock] = useState<'somatic' | 'cognitive' | 'friction' | 'support'>('somatic');
  const [demoCyclesCompleted, setDemoCyclesCompleted] = useState<number>(0);
  const [softPauseSeconds, setSoftPauseSeconds] = useState<number>(10);

  // Demo Cognitive Block State
  const [demoCbtCard, setDemoCbtCard] = useState<'thought' | 'reframe'>('thought');
  const [demoCravingSlider, setDemoCravingSlider] = useState<number>(7);
  const [demoSelectedDistortions, setDemoSelectedDistortions] = useState<string[]>(['Dopamine Loop']);
  const [demoReflectionText, setDemoReflectionText] = useState<string>('');

  // Demo Intentional Friction Block State
  const [demoFrictionDuration, setDemoFrictionDuration] = useState<number>(10);
  const [demoFrictionRemaining, setDemoFrictionRemaining] = useState<number>(10);
  const [demoFrictionRunning, setDemoFrictionRunning] = useState<boolean>(false);
  const [demoSelectedValues, setDemoSelectedValues] = useState<string[]>(['Restorative Sleep']);

  // Demo Therapist Audio & Support State
  const [demoAudioPlaying, setDemoAudioPlaying] = useState<boolean>(false);
  const [demoAudioPlayhead, setDemoAudioPlayhead] = useState<number>(0);
  const [demoAudioMuted, setDemoAudioMuted] = useState<boolean>(false);

  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [legalModalOpen, setLegalModalOpen] = useState<'privacy' | 'terms' | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState<boolean>(false);
  const [checkoutPlan, setCheckoutPlan] = useState<StripeCheckoutPlan | null>(null);
  const [isCustomerPortalOpen, setIsCustomerPortalOpen] = useState<boolean>(false);

  const handleOpenBilling = () => {
    if (onOpenBillingPortal) {
      onOpenBillingPortal();
    } else {
      setIsCustomerPortalOpen(true);
    }
  };

  // Responsive Mobile Navigation Drawer State
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  // Composable Block Feature Studio Interactive State
  const [somaticCycle, setSomaticCycle] = useState<'box_4_4_4' | 'relaxing_4_7_8' | 'diaphragmatic_mandala'>('box_4_4_4');
  const [somaticPhase, setSomaticPhase] = useState<'Inhale' | 'Hold' | 'Exhale'>('Inhale');
  const [somaticCountdown, setSomaticCountdown] = useState<number>(4);
  const [cravingSliderVal, setCravingSliderVal] = useState<number>(7);
  const [activeCbtCard, setActiveCbtCard] = useState<'thought' | 'reframe'>('thought');
  const [frictionDuration, setFrictionDuration] = useState<number>(60);
  const [frictionCountdown, setFrictionCountdown] = useState<number>(60);
  const [isFrictionRunning, setIsFrictionRunning] = useState<boolean>(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(['Restorative Sleep', 'Mental Clarity']);
  const [isPlayingSupportAudio, setIsPlayingSupportAudio] = useState<boolean>(false);
  const [supportPlayhead, setSupportPlayhead] = useState<number>(0);

  // Close mobile drawer on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Breathing pacer cycle (Only runs when demoActive is true and not completed)
  useEffect(() => {
    if (!demoActive || demoCompleted) return;

    const interval = setInterval(() => {
      setBreathSeconds((prev) => {
        if (prev > 1) return prev - 1;
        setBreathPhase((current) => {
          if (current === 'Inhale') return 'Hold';
          if (current === 'Hold') return 'Exhale';
          setDemoCyclesCompleted((c) => c + 1);
          return 'Inhale';
        });
        return 4;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [demoActive, demoCompleted]);

  // Tier 1 Soft Pause Countdown (10s)
  useEffect(() => {
    if (!demoActive || demoCompleted || demoActiveTier !== 1) return;
    if (softPauseSeconds <= 0) return;
    const interval = setInterval(() => {
      setSoftPauseSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [demoActive, demoCompleted, demoActiveTier, softPauseSeconds]);

  // Demo Friction Delay Countdown Timer
  useEffect(() => {
    if (!demoActive || demoCompleted || !demoFrictionRunning) return;
    const interval = setInterval(() => {
      setDemoFrictionRemaining((prev) => {
        if (prev <= 1) {
          setDemoFrictionRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [demoActive, demoCompleted, demoFrictionRunning]);

  // Demo Support Audio Playhead Simulation
  useEffect(() => {
    if (!demoActive || demoCompleted || !demoAudioPlaying) return;
    const interval = setInterval(() => {
      setDemoAudioPlayhead((prev) => (prev >= 45 ? 0 : prev + 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [demoActive, demoCompleted, demoAudioPlaying]);

  // Somatic Wave Pacer Timer for Feature Studio
  useEffect(() => {
    const interval = setInterval(() => {
      setSomaticCountdown((prev) => {
        if (prev > 1) return prev - 1;
        if (somaticCycle === 'box_4_4_4') {
          setSomaticPhase((curr) => (curr === 'Inhale' ? 'Hold' : curr === 'Hold' ? 'Exhale' : 'Inhale'));
          return 4;
        } else if (somaticCycle === 'relaxing_4_7_8') {
          if (somaticPhase === 'Inhale') {
            setSomaticPhase('Hold');
            return 7;
          } else if (somaticPhase === 'Hold') {
            setSomaticPhase('Exhale');
            return 8;
          } else {
            setSomaticPhase('Inhale');
            return 4;
          }
        } else {
          // Diaphragmatic Mandala (5s in, 5s out)
          setSomaticPhase((curr) => (curr === 'Inhale' ? 'Exhale' : 'Inhale'));
          return 5;
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [somaticCycle, somaticPhase]);

  // Friction Delay Countdown Timer for Feature Studio
  useEffect(() => {
    if (!isFrictionRunning) return;
    const interval = setInterval(() => {
      setFrictionCountdown((prev) => {
        if (prev <= 1) {
          setIsFrictionRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isFrictionRunning]);

  // Support Voice Note Audio Playhead Simulation
  useEffect(() => {
    if (!isPlayingSupportAudio) return;
    const interval = setInterval(() => {
      setSupportPlayhead((prev) => (prev >= 45 ? 0 : prev + 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlayingSupportAudio]);

  const handleSelectTier = (tier: 1 | 2 | 3 | 4 | 5) => {
    setDemoActiveTier(tier);
    let duration = 10;
    if (tier === 1) duration = 10;
    else if (tier === 2) duration = 90;
    else if (tier === 3) duration = 180;
    else if (tier === 4) duration = 120;
    else if (tier === 5) duration = 0;

    setDemoFrictionDuration(duration);
    setDemoFrictionRemaining(duration);
    setDemoFrictionRunning(false);
    if (tier === 1) setSoftPauseSeconds(10);
  };

  const handleSelectBlock = (block: 'somatic' | 'cognitive' | 'friction' | 'support') => {
    setDemoActiveBlock(block);
  };

  const toggleDemoValue = (val: string) => {
    setDemoSelectedValues((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const toggleDemoDistortion = (tag: string) => {
    setDemoSelectedDistortions((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const isContinueLocked = (): boolean => {
    if (demoActiveTier === 1) {
      return softPauseSeconds > 0;
    }
    if (demoActiveTier === 2) {
      return !(demoCbtCard === 'reframe' || demoReflectionText.trim().length > 0);
    }
    if (demoActiveTier === 3) {
      return demoCyclesCompleted < 1;
    }
    if (demoActiveTier === 4) {
      return demoFrictionRemaining > 0 || demoSelectedValues.length === 0;
    }
    if (demoActiveTier === 5) {
      return true;
    }
    return false;
  };

  const getDemoAudioCaption = (playhead: number): string => {
    if (playhead < 12) {
      return 'Take a slow, grounding breath. This urge you are feeling right now is just physiological arousal, not a mandate to act.';
    } else if (playhead < 24) {
      return 'Notice where in your body the tension lives. Relax your jaw, lower your shoulders, and let the breath flow down.';
    } else if (playhead < 36) {
      return 'Your values are bigger than this momentary autopilot impulse. You have the freedom to choose your response.';
    } else {
      return 'You are doing the hard, beautiful work of rewiring your habits. Step back whenever you are ready.';
    }
  };

  const startDemo = (cat?: DemoCategory) => {
    if (cat) setActiveCategory(cat);
    setDemoActive(true);
    setDemoCompleted(false);
    setBreathPhase('Inhale');
    setBreathSeconds(4);
    setDemoCyclesCompleted(0);
    setSoftPauseSeconds(10);
    setDemoCbtCard('thought');
    setDemoReflectionText('');
    setDemoAudioPlayhead(0);
    setDemoAudioPlaying(false);
    setUrgeRating(8);
    setPostUrgeRating(3);
    const duration = demoActiveTier === 1 ? 10 : demoActiveTier === 2 ? 90 : demoActiveTier === 3 ? 180 : demoActiveTier === 4 ? 120 : 0;
    setDemoFrictionDuration(duration);
    setDemoFrictionRemaining(duration);
    setDemoFrictionRunning(false);
  };

  const scenario = DEMO_SCENARIOS[activeCategory];

  return (
    <div style={{ backgroundColor: '#FAF8F5', color: '#1E293B', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Responsive Stylesheet for Breakpoints */}
      <style>{`
        @media (max-width: 767px) {
          .luminous-desktop-nav {
            display: none !important;
          }
          .luminous-mobile-nav-trigger {
            display: flex !important;
          }
          .luminous-hero-title {
            font-size: 32px !important;
            line-height: 1.25 !important;
          }
          .luminous-studio-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (min-width: 768px) and (max-width: 1050px) {
          .luminous-desktop-nav {
            gap: 8px !important;
            flex-wrap: nowrap !important;
          }
          .luminous-desktop-nav a {
            font-size: 11.5px !important;
            padding: 2px 4px !important;
            white-space: nowrap !important;
          }
          .luminous-desktop-nav button {
            padding: 6px 10px !important;
            font-size: 11px !important;
            white-space: nowrap !important;
          }
        }
        @media (min-width: 768px) {
          .luminous-desktop-nav {
            display: flex !important;
          }
          .luminous-mobile-nav-trigger {
            display: none !important;
          }
          .luminous-mobile-drawer-root {
            display: none !important;
          }
        }
      `}</style>
      
      {/* Skip to main content link for keyboard & screen reader accessibility */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          top: '-50px',
          left: '16px',
          backgroundColor: '#2D2826',
          color: '#FAF8F5',
          padding: '10px 18px',
          borderRadius: '8px',
          zIndex: 9999,
          fontWeight: 700,
          fontSize: '13px',
          textDecoration: 'none',
          transition: 'top 0.15s ease',
        }}
        onFocus={(e) => (e.currentTarget.style.top = '16px')}
        onBlur={(e) => (e.currentTarget.style.top = '-50px')}
      >
        Skip to main content
      </a>

      {/* Top Banner */}
      <div style={{
        background: 'linear-gradient(90deg, #FBF6EF 0%, #F5EAE0 50%, #FBF6EF 100%)',
        color: '#5C4A3E',
        padding: '10px 24px',
        textAlign: 'center',
        fontSize: '13px',
        fontWeight: 600,
        borderBottom: '1px solid #EAE0D5',
        letterSpacing: '0.2px',
      }}>
        🌅 Luminous Break is now available globally across iOS, Android, and Desktop Browsers.
      </div>

      {/* Navigation Header */}
      <header style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #EAE2D7',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LuminousBreakLogo size={46} showText={true} subtitle="Mindful Habits & Digital Wellbeing" />
        </div>

        {/* Desktop Navigation */}
        <nav
          className="luminous-desktop-nav"
          aria-label="Main Navigation"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          <a href="#how-it-works" style={{ textDecoration: 'none', color: '#5C524C', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>How It Works</a>
          <a href="#ecosystem" style={{ textDecoration: 'none', color: '#5C524C', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>Apps & Ecosystem</a>
          <a href="#composable-studio" style={{ textDecoration: 'none', color: '#5C524C', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>Feature Studio</a>
          <a href="#wellness-pillars" style={{ textDecoration: 'none', color: '#5C524C', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>9 Habit Areas</a>
          <a href="#pricing" style={{ textDecoration: 'none', color: '#5C524C', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>Plans & Pricing</a>
          <a href="#faq" style={{ textDecoration: 'none', color: '#5C524C', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>FAQ</a>
          <a href="#privacy-promise" style={{ textDecoration: 'none', color: '#5C524C', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>Privacy Promise</a>

          <button
            onClick={handleOpenBilling}
            aria-label="Individual Account & Billing"
            style={{
              backgroundColor: '#FAF8F5',
              border: '1.5px solid #D1C7BD',
              color: '#2D2826',
              borderRadius: '999px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'all 0.15s ease',
            }}
          >
            💳 Individual Billing
          </button>

          <button
            onClick={onOpenPortal}
            style={{
              background: 'linear-gradient(135deg, #749C8F 0%, #5E8276 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '999px',
              padding: '8px 18px',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(94, 130, 118, 0.25)',
              letterSpacing: '0.2px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            🩺 Clinician Sign In
          </button>
        </nav>

        {/* Mobile Hamburger Toggle Button (≥ 48x48 dp touch target) */}
        <button
          className="luminous-mobile-nav-trigger"
          aria-expanded={isMobileMenuOpen}
          aria-label="Toggle Menu"
          aria-controls="mobile-nav-drawer"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            display: 'none',
            minWidth: '48px',
            minHeight: '48px',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#FAF8F5',
            border: '1.5px solid #D1C7BD',
            borderRadius: '10px',
            cursor: 'pointer',
            padding: '10px',
            fontSize: '22px',
            color: '#2D2826',
            lineHeight: 1,
          }}
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      </header>

      {/* Responsive Slide-out Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <div className="luminous-mobile-drawer-root">
          {/* Backdrop for closing on outside tap */}
          <div
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(29, 25, 24, 0.55)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998,
            }}
          />

          {/* Drawer Dialog Container */}
          <div
            id="mobile-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation Menu"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(320px, 85vw)',
              backgroundColor: '#FFFFFF',
              zIndex: 9999,
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-8px 0 28px rgba(0,0,0,0.18)',
              overflowY: 'auto',
            }}
          >
            {/* Drawer Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EAE2D7', paddingBottom: '16px', marginBottom: '20px' }}>
              <LuminousBreakLogo size={36} showText={true} />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close Navigation Menu"
                style={{
                  minWidth: '48px',
                  minHeight: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'none',
                  border: '1px solid #EAE2D7',
                  borderRadius: '8px',
                  fontSize: '18px',
                  color: '#5C524C',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            {/* Navigation Links with Touch Targets >= 48x48 dp */}
            <nav aria-label="Mobile Navigation Links" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
              <a
                href="#how-it-works"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  color: '#2D2826',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: '#FAF8F5',
                }}
              >
                How It Works
              </a>
              <a
                href="#ecosystem"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  color: '#2D2826',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: '#FAF8F5',
                }}
              >
                Apps & Ecosystem
              </a>
              <a
                href="#composable-studio"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  color: '#2D2826',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: '#FAF8F5',
                }}
              >
                Feature Studio
              </a>
              <a
                href="#wellness-pillars"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  color: '#2D2826',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: '#FAF8F5',
                }}
              >
                9 Habit Areas
              </a>
              <a
                href="#pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  color: '#2D2826',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: '#FAF8F5',
                }}
              >
                Plans & Pricing
              </a>
              <a
                href="#faq"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  color: '#2D2826',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: '#FAF8F5',
                }}
              >
                FAQ
              </a>
              <a
                href="#privacy-promise"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  color: '#2D2826',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: '#FAF8F5',
                }}
              >
                Our Privacy Promise
              </a>
            </nav>

            {/* Action Buttons in Mobile Drawer (>= 48x48 dp) */}
            <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid #EAE2D7', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleOpenBilling();
                }}
                aria-label="Individual Account & Billing"
                style={{
                  width: '100%',
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  backgroundColor: '#FAF8F5',
                  border: '1.5px solid #D1C7BD',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#2D2826',
                  cursor: 'pointer',
                }}
              >
                <span>💳</span>
                <span>Individual Account & Billing</span>
              </button>

              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenPortal();
                }}
                style={{
                  width: '100%',
                  minHeight: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #749C8F 0%, #5E8276 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(94, 130, 118, 0.25)',
                }}
              >
                🩺 Clinician Sign In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Landmark for WCAG 2.1 AA Compliance */}
      <main id="main-content">

      {/* Hero Section */}
      <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '64px 24px 40px', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'linear-gradient(135deg, #FFF3EC 0%, #FDF0E2 100%)',
          border: '1px solid #F6D0B8',
          color: '#D96B4F',
          padding: '7px 18px',
          borderRadius: '999px',
          fontSize: '13px',
          fontWeight: 700,
          marginBottom: '22px',
          boxShadow: '0 2px 8px rgba(224, 122, 95, 0.08)',
        }}>
          <span>🌅 A gentle pause before digital impulse</span>
        </div>

        <h1 style={{
          fontSize: '48px',
          fontWeight: 800,
          color: '#2D2826',
          letterSpacing: '-1.5px',
          lineHeight: '1.2',
          maxWidth: '850px',
          margin: '0 auto 18px',
        }}>
          Reclaim Your Attention & Peace of Mind.
        </h1>

        <p style={{
          fontSize: '18px',
          color: '#5C524C',
          maxWidth: '720px',
          margin: '0 auto 32px',
          lineHeight: '1.65',
        }}>
          Break free from mindless scrolling, late-night impulse spending, and high-stress screen habits. Luminous Break gives you a calming, supportive moment of clarity <em>before</em> autopilot takes over.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              const el = document.getElementById('interactive-demo');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
              startDemo('financial');
            }}
            style={{
              background: 'linear-gradient(135deg, #C85A3D 0%, #E07A5F 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '12px',
              padding: '16px 34px',
              minHeight: '48px',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(200, 90, 61, 0.32)',
              letterSpacing: '0.2px',
            }}
          >
            ▶️ Test an Interactive Pause
          </button>

          <button
            onClick={onOpenPortal}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#4D7567',
              border: '2px solid #749C8F',
              borderRadius: '12px',
              padding: '16px 30px',
              minHeight: '48px',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            🩺 For Therapists & Clinics
          </button>
        </div>

        {/* ========================================================================= */}
        {/* INTERACTIVE MINDFUL PAUSE HERO SHOWCASE                                   */}
        {/* ========================================================================= */}
        <div id="interactive-demo" style={{
          marginTop: '44px',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          padding: '32px',
          maxWidth: '740px',
          margin: '44px auto 0',
          boxShadow: '0 20px 50px rgba(224, 122, 95, 0.12), 0 4px 20px rgba(94, 75, 66, 0.05)',
          border: '2px solid #F4A261',
          textAlign: 'left',
        }}>
          {/* Header Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EAE2D7', paddingBottom: '16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <LuminousBreakLogo size={32} showText={true} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ backgroundColor: '#FFF3EC', color: '#9E3C22', fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '999px', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                Live Product Demo
              </span>
            </div>
          </div>

          {/* Trigger Context Ribbon */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', backgroundColor: '#FAF8F5', padding: '12px 18px', borderRadius: '12px', border: '1px solid #EAE2D7', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>{scenario.icon}</span>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <strong style={{ fontSize: '14px', color: '#2D2826' }}>{scenario.title}</strong>
                  <span style={{ backgroundColor: '#EBF3F0', color: '#26594A', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>
                    {scenario.technique}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#5C524C', marginTop: '2px' }}>
                  Simulated intercept when opening: <strong>{scenario.triggerApp}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Category Scenario Switcher Tabs */}
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '20px' }}>
            {(Object.keys(DEMO_SCENARIOS) as DemoCategory[]).map((key) => {
              const item = DEMO_SCENARIOS[key];
              const isSelected = activeCategory === key;
              return (
                <button
                  key={key}
                  onClick={() => startDemo(key)}
                  aria-pressed={isSelected}
                  style={{
                    padding: '8px 14px',
                    minHeight: '38px',
                    borderRadius: '8px',
                    border: isSelected ? '1.5px solid #9E3C22' : '1px solid #EAE2D7',
                    backgroundColor: isSelected ? '#FFF3EC' : '#FAF8F5',
                    color: isSelected ? '#9E3C22' : '#5C524C',
                    fontSize: '12px',
                    fontWeight: isSelected ? 700 : 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span>{item.icon}</span>
                  <span>{item.title}</span>
                </button>
              );
            })}
          </div>

          {/* Strictness Tier Escalation Stepper */}
          <div style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#5C524C', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                Strictness Tier Escalation Stepper:
              </span>
              <span style={{ fontSize: '11px', color: '#8A8079' }}>
                Modulates cooldown duration & anti-bypass gates
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px', marginBottom: '10px' }}>
              {([1, 2, 3, 4, 5] as const).map((tier) => {
                const config = DEMO_TIER_CONFIGS[tier];
                const isSelected = demoActiveTier === tier;
                return (
                  <button
                    key={tier}
                    onClick={() => handleSelectTier(tier)}
                    aria-pressed={isSelected}
                    style={{
                      padding: '8px 10px',
                      minHeight: '44px',
                      borderRadius: '8px',
                      border: isSelected ? `2px solid ${config.border}` : '1px solid #EAE2D7',
                      backgroundColor: isSelected ? config.bg : '#FAF8F5',
                      color: isSelected ? config.color : '#5C524C',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: '11px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '2px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontWeight: 800, whiteSpace: 'nowrap' }}>Tier {tier}</span>
                    <span style={{ fontSize: '10px', opacity: 0.9, whiteSpace: 'nowrap' }}>{tier === 1 ? 'Soft Pause (10s)' : tier === 2 ? 'Journal (90s)' : tier === 3 ? 'Somatic (3m)' : tier === 4 ? 'Contract (120s)' : 'Hard Lock (Curfew)'}</span>
                  </button>
                );
              })}
            </div>
            {/* Contextual Clinical Purpose Info Pill */}
            <div style={{
              backgroundColor: DEMO_TIER_CONFIGS[demoActiveTier].bg,
              color: DEMO_TIER_CONFIGS[demoActiveTier].color,
              border: `1px solid ${DEMO_TIER_CONFIGS[demoActiveTier].border}`,
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '1.4',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span>ℹ️</span>
              <span>{DEMO_TIER_CONFIGS[demoActiveTier].info}</span>
            </div>
          </div>

          {/* Composable Block Switcher Tabs */}
          <div role="tablist" aria-label="Composable Intervention Blocks" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '20px' }}>
            {DEMO_BLOCK_TABS.map((block) => {
              const isSelected = demoActiveBlock === block.id;
              return (
                <button
                  key={block.id}
                  role="tab"
                  aria-selected={isSelected}
                  onClick={() => handleSelectBlock(block.id)}
                  style={{
                    padding: '10px 18px',
                    minHeight: '44px',
                    borderRadius: '10px',
                    border: isSelected ? '2px solid #C85A3D' : '1px solid #EAE2D7',
                    backgroundColor: isSelected ? '#FFF3EC' : '#FFFFFF',
                    color: isSelected ? '#9E3C22' : '#5C524C',
                    fontWeight: isSelected ? 800 : 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: isSelected ? '0 2px 8px rgba(200, 90, 61, 0.15)' : 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{block.icon}</span>
                  <span>{block.label}</span>
                </button>
              );
            })}
          </div>

            {/* Guided Breathing Circle or Idle Preview Card */}
            {!demoActive ? (
              <div style={{
                backgroundColor: '#FAF8F5',
                borderRadius: '16px',
                padding: '36px 24px',
                border: '1.5px dashed #D1C7BD',
                textAlign: 'center',
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#EBF3F0',
                  color: '#1B4332',
                  padding: '6px 14px',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: 700,
                  marginBottom: '18px',
                }}>
                  <span>🌿 Tranquil Pause Preview • Autoplay Idle</span>
                </div>

                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  margin: '0 auto 18px',
                  backgroundColor: '#F5EFEB',
                  border: '3px solid #749C8F',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 6px 18px rgba(94, 130, 118, 0.15)',
                }}>
                  <span style={{ fontSize: '36px' }}>
                    {demoActiveBlock === 'somatic' ? '🌊' : demoActiveBlock === 'cognitive' ? '🧠' : demoActiveBlock === 'friction' ? '⏳' : '🎙️'}
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#5C524C', marginTop: '4px' }}>
                    Tier {demoActiveTier} Idle
                  </span>
                </div>

                <div style={{ maxWidth: '520px', margin: '0 auto 24px' }}>
                  <p style={{ fontSize: '15px', color: '#2D2826', fontWeight: 600, lineHeight: '1.5', margin: '0 0 8px 0' }}>
                    "{scenario.prompt}"
                  </p>
                  <span style={{ fontSize: '12px', color: '#5C524C' }}>
                    Simulated intercept for {scenario.triggerApp}. Tap below to begin testing the {DEMO_BLOCK_TABS.find(b => b.id === demoActiveBlock)?.label} under {DEMO_TIER_CONFIGS[demoActiveTier].shortLabel}.
                  </span>
                </div>

                <button
                  onClick={() => startDemo(activeCategory)}
                  style={{
                    background: 'linear-gradient(135deg, #C85A3D 0%, #E07A5F 100%)',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '14px 28px',
                    minHeight: '48px',
                    fontSize: '15px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(200, 90, 61, 0.3)',
                    letterSpacing: '0.2px',
                  }}
                >
                  ▶️ Test an Interactive Pause
                </button>
              </div>
            ) : !demoCompleted ? (
              <div>
                {demoActiveTier === 5 ? (
                  /* Tier 5 Hard Lock Sleep Shield */
                  <div style={{
                    backgroundColor: '#181F1C',
                    borderRadius: '16px',
                    padding: '32px 24px',
                    border: '2px solid #E07A5F',
                    boxShadow: '0 8px 30px rgba(224, 122, 95, 0.25)',
                    color: '#F5EFEB',
                    textAlign: 'center',
                  }}>
                    <div style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(224, 122, 95, 0.15)',
                      border: '2px solid #E07A5F',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px',
                      margin: '0 auto 16px',
                      boxShadow: '0 0 20px rgba(224, 122, 95, 0.3)',
                    }}>
                      🔒
                    </div>

                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: '#7F1D1D',
                      color: '#FECACA',
                      padding: '6px 14px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 700,
                      marginBottom: '16px',
                    }}>
                      <span>🌙 Scheduled Sleep Curfew Shield Active (10:00 PM – 6:00 AM)</span>
                    </div>

                    <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#FFFFFF', margin: '0 0 10px 0' }}>
                      App Access Suspended for Restorative Sleep
                    </h3>
                    <p style={{ fontSize: '14px', color: '#D1C7BD', maxWidth: '520px', margin: '0 auto 20px', lineHeight: '1.6' }}>
                      App access is strictly suspended during scheduled recovery hours to protect sleep hygiene and biological rest. Vulnerable late-night dopamine loops are interrupted with an absolute barrier.
                    </p>

                    <div style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid #EF4444',
                      borderRadius: '10px',
                      padding: '12px 18px',
                      maxWidth: '480px',
                      margin: '0 auto 24px',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#FCA5A5',
                    }}>
                      🚫 Zero Bypass Permitted • Unlock button disabled until 6:00 AM
                    </div>

                    {/* 988 Crisis Dialer */}
                    <div style={{
                      backgroundColor: '#242C28',
                      borderRadius: '12px',
                      padding: '16px',
                      maxWidth: '440px',
                      margin: '0 auto',
                      border: '1px solid #3A4741',
                    }}>
                      <div style={{ fontSize: '12px', color: '#A09890', marginBottom: '8px' }}>
                        Experiencing acute nighttime distress or need mental health crisis support?
                      </div>
                      <a
                        href="tel:988"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          backgroundColor: '#DC2626',
                          color: '#FFFFFF',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontWeight: 700,
                          fontSize: '14px',
                          minHeight: '48px',
                          boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)',
                        }}
                      >
                        📞 Call 988 Suicide & Crisis Lifeline (24/7)
                      </a>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Block 1: Somatic Pacer */}
                    {demoActiveBlock === 'somatic' && (
                      <div style={{ backgroundColor: '#FAF8F5', borderRadius: '16px', padding: '28px', border: '1px solid #EAE2D7', textAlign: 'center' }}>
                        <div
                          aria-live="polite"
                          aria-label={`Breathing pacer: ${breathPhase}, 00:0${breathSeconds}`}
                          style={{
                            width: '130px',
                            height: '130px',
                            borderRadius: '50%',
                            margin: '0 auto 20px',
                            backgroundColor: breathPhase === 'Inhale' ? '#FFEBDD' : breathPhase === 'Hold' ? '#FFF7E6' : '#EBF5F1',
                            border: `4px solid ${breathPhase === 'Inhale' ? '#E07A5F' : breathPhase === 'Hold' ? '#D9941A' : '#5E8276'}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 4s ease-in-out, background-color 1s ease',
                            transform: breathPhase === 'Inhale' ? 'scale(1.15)' : breathPhase === 'Hold' ? 'scale(1.15)' : 'scale(0.9)',
                            boxShadow: `0 8px 24px ${breathPhase === 'Inhale' ? 'rgba(224, 122, 95, 0.25)' : breathPhase === 'Hold' ? 'rgba(246, 189, 96, 0.25)' : 'rgba(116, 156, 143, 0.25)'}`,
                          }}
                        >
                          <span style={{
                            fontSize: '13px',
                            fontWeight: 800,
                            color: breathPhase === 'Inhale' ? '#9E3C22' : breathPhase === 'Hold' ? '#7A4D00' : '#204A3C',
                            textTransform: 'uppercase',
                            letterSpacing: '0.8px',
                          }}>
                            {breathPhase}
                          </span>
                          <span style={{
                            fontSize: '24px',
                            fontWeight: 800,
                            color: breathPhase === 'Inhale' ? '#9E3C22' : breathPhase === 'Hold' ? '#7A4D00' : '#204A3C',
                            fontFamily: 'monospace',
                          }}>
                            00:0{breathSeconds}
                          </span>
                        </div>

                        {/* Cycle Counter & Phase Guidance */}
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#FFFFFF', border: '1px solid #EAE2D7', borderRadius: '999px', padding: '4px 14px', marginBottom: '14px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#5C524C' }}>
                            Cycle {demoCyclesCompleted + 1} of 3
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: demoCyclesCompleted >= 1 ? '#26594A' : '#8A8079', backgroundColor: demoCyclesCompleted >= 1 ? '#EBF3F0' : '#F5EFEB', padding: '2px 8px', borderRadius: '999px' }}>
                            {demoCyclesCompleted >= 1 ? '✓ Target Completed (1+ Cycles)' : 'Gating Active (Needs 1 Cycle)'}
                          </span>
                        </div>

                        <div style={{ maxWidth: '520px', margin: '0 auto 18px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: breathPhase === 'Inhale' ? '#9E3C22' : breathPhase === 'Hold' ? '#7A4D00' : '#26594A', marginBottom: '6px' }}>
                            {breathPhase === 'Inhale' && 'Slow gentle inhalation through the nose into lower belly.'}
                            {breathPhase === 'Hold' && 'Hold breath gently with stillness, observing sensation without judgment.'}
                            {breathPhase === 'Exhale' && 'Smooth, extended exhalation through relaxed lips, releasing muscle tension.'}
                          </div>
                          <p style={{ fontSize: '14px', color: '#2D2826', fontWeight: 600, lineHeight: '1.5', margin: '0 0 6px 0' }}>
                            "{scenario.prompt}"
                          </p>
                          <span style={{ fontSize: '12px', color: '#5C524C' }}>
                            Giving your prefrontal cortex time to downregulate autonomic arousal and interrupt the compulsion loop.
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Block 2: Cognitive Reframing */}
                    {demoActiveBlock === 'cognitive' && (
                      <div style={{ backgroundColor: '#FAF8F5', borderRadius: '16px', padding: '24px', border: '1px solid #EAE2D7' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                          <strong style={{ fontSize: '15px', color: '#2D2826' }}>Cognitive Reframing (CBT Dialectical Inquiry)</strong>
                          <span style={{ backgroundColor: '#FFF3EC', color: '#9E3C22', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>
                            Prefrontal Cortical Re-engagement
                          </span>
                        </div>

                        {/* Automatic Thought vs Rational Reframe Toggle */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                          <button
                            onClick={() => setDemoCbtCard('thought')}
                            style={{
                              padding: '12px',
                              borderRadius: '10px',
                              border: demoCbtCard === 'thought' ? '2px solid #E07A5F' : '1px solid #EAE2D7',
                              backgroundColor: demoCbtCard === 'thought' ? '#FFF3EC' : '#FFFFFF',
                              color: demoCbtCard === 'thought' ? '#9E3C22' : '#5C524C',
                              fontWeight: 700,
                              fontSize: '13px',
                              cursor: 'pointer',
                              textAlign: 'left',
                              minHeight: '44px',
                            }}
                          >
                            <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.8, marginBottom: '2px' }}>Urge Mindset</div>
                            <div>⚡ Automatic Urge Thought</div>
                          </button>

                          <button
                            onClick={() => setDemoCbtCard('reframe')}
                            style={{
                              padding: '12px',
                              borderRadius: '10px',
                              border: demoCbtCard === 'reframe' ? '2px solid #749C8F' : '1px solid #EAE2D7',
                              backgroundColor: demoCbtCard === 'reframe' ? '#EBF3F0' : '#FFFFFF',
                              color: demoCbtCard === 'reframe' ? '#26594A' : '#5C524C',
                              fontWeight: 700,
                              fontSize: '13px',
                              cursor: 'pointer',
                              textAlign: 'left',
                              minHeight: '44px',
                            }}
                          >
                            <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.8, marginBottom: '2px' }}>Rational Agency</div>
                            <div>🧠 Rational Reframe (Click to Apply)</div>
                          </button>
                        </div>

                        {/* Active CBT Card Content */}
                        <div style={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '12px',
                          padding: '16px',
                          border: `1px solid ${demoCbtCard === 'thought' ? '#F6D0B8' : '#B8D8CE'}`,
                          marginBottom: '16px',
                        }}>
                          {demoCbtCard === 'thought' ? (
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#DC2626', marginBottom: '4px' }}>
                                Automatic Habit Loop Distortion:
                              </div>
                              <p style={{ fontSize: '14px', color: '#2D2826', margin: '0 0 6px 0', fontWeight: 600 }}>
                                "{scenario.prompt}"
                              </p>
                              <span style={{ fontSize: '12px', color: '#8A8079' }}>
                                Notice how impulsive dopamine signals disguise wants as immediate urgent needs.
                              </span>
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#26594A', marginBottom: '4px' }}>
                                ✓ Grounded Cognitive Reframe Applied:
                              </div>
                              <p style={{ fontSize: '14px', color: '#1B4332', margin: '0 0 6px 0', fontWeight: 600 }}>
                                "This urge is an artificial dopamine spike, not an emergency. 83% of momentum impulses fade within minutes. Taking space protects my core agency and long-term peace."
                              </p>
                              <span style={{ fontSize: '12px', color: '#5C524C' }}>
                                Prefrontal cortex engaged. Tier 2 completion criteria satisfied.
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Reflection Text Input */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#5C524C', marginBottom: '6px' }}>
                            Mindful Reflection Journal (Tier 2 Alternative Unlock):
                          </label>
                          <input
                            type="text"
                            value={demoReflectionText}
                            onChange={(e) => setDemoReflectionText(e.target.value)}
                            placeholder="Enter a 1-sentence mindful intention or reflection to disarm the urge..."
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              borderRadius: '8px',
                              border: '1px solid #D1C7BD',
                              fontSize: '13px',
                              boxSizing: 'border-box',
                              backgroundColor: '#FFFFFF',
                              color: '#2D2826',
                            }}
                          />
                        </div>

                        {/* Craving vs Need Intensity Slider */}
                        <div style={{ backgroundColor: '#FFFFFF', padding: '14px 18px', borderRadius: '12px', border: '1px solid #EAE2D7', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#2D2826' }}>
                              Craving vs. Genuine Need Intensity:
                            </span>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              padding: '3px 10px',
                              borderRadius: '999px',
                              backgroundColor: demoCravingSlider >= 7 ? '#FEE2E2' : demoCravingSlider >= 4 ? '#FEF3C7' : '#DCFCE7',
                              color: demoCravingSlider >= 7 ? '#991B1B' : demoCravingSlider >= 4 ? '#92400E' : '#166534',
                            }}>
                              Level {demoCravingSlider}: {demoCravingSlider >= 9 ? 'Severe Autopilot Distress' : demoCravingSlider >= 7 ? 'Acute Craving Wave' : demoCravingSlider >= 4 ? 'Moderate Urge' : 'Mild Impulse'}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={demoCravingSlider}
                            onChange={(e) => setDemoCravingSlider(Number(e.target.value))}
                            style={{ width: '100%', cursor: 'pointer' }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#8A8079', marginTop: '4px' }}>
                            <span>1 (Mild Want)</span>
                            <span>5 (Moderate)</span>
                            <span>10 (Overwhelming Surge)</span>
                          </div>
                        </div>

                        {/* Clickable Distortion Tags */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#5C524C', marginBottom: '8px' }}>
                            Identified Cognitive Distortion Tags (Click to toggle):
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {DEMO_DISTORTION_TAGS.map((tag) => {
                              const active = demoSelectedDistortions.includes(tag);
                              return (
                                <button
                                  key={tag}
                                  onClick={() => toggleDemoDistortion(tag)}
                                  style={{
                                    padding: '6px 12px',
                                    minHeight: '34px',
                                    borderRadius: '999px',
                                    border: active ? '1.5px solid #9E3C22' : '1px solid #D1C7BD',
                                    backgroundColor: active ? '#FFF3EC' : '#FFFFFF',
                                    color: active ? '#9E3C22' : '#5C524C',
                                    fontSize: '12px',
                                    fontWeight: active ? 700 : 500,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {active ? '✓ ' : '+ '}{tag}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Loss Probability Curve Card */}
                        <div style={{ backgroundColor: '#FAF1EB', borderRadius: '10px', padding: '12px 16px', border: '1px solid #F6D0B8', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '24px' }}>📉</span>
                          <div style={{ fontSize: '12px', color: '#733322', lineHeight: '1.4' }}>
                            <strong>Consequence & Loss Probability:</strong> Clinical telemetry confirms 83% of impulsive {scenario.title.toLowerCase()} triggers yield regret within 2 hours. Choosing intentional pause safeguards compounding peace.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Block 3: Intentional Friction */}
                    {demoActiveBlock === 'friction' && (
                      <div style={{ backgroundColor: '#FAF8F5', borderRadius: '16px', padding: '24px', border: '1px solid #EAE2D7', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                          <strong style={{ fontSize: '15px', color: '#2D2826' }}>Intentional Friction (Cooling Delay & Values Contract)</strong>
                          <span style={{ backgroundColor: '#EBF3F0', color: '#26594A', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>
                            Tier {demoActiveTier} Friction: {demoFrictionDuration}s
                          </span>
                        </div>

                        {/* Countdown Timer Display */}
                        <div style={{
                          fontSize: '44px',
                          fontWeight: 800,
                          fontFamily: 'monospace',
                          color: demoFrictionRemaining === 0 ? '#26594A' : '#9E3C22',
                          margin: '12px 0 8px',
                        }}>
                          {String(Math.floor(demoFrictionRemaining / 60)).padStart(2, '0')}:{String(demoFrictionRemaining % 60).padStart(2, '0')}
                        </div>
                        <div style={{ fontSize: '12px', color: '#5C524C', marginBottom: '16px' }}>
                          {demoFrictionRemaining === 0 ? '✓ Cooling delay complete! Review your values below to continue.' : 'Time-locked delay to let autonomic arousal return to baseline.'}
                        </div>

                        {/* Animated Progress Bar */}
                        <div style={{
                          height: '10px',
                          backgroundColor: '#EAE2D7',
                          borderRadius: '999px',
                          overflow: 'hidden',
                          maxWidth: '440px',
                          margin: '0 auto 20px',
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${demoFrictionDuration > 0 ? Math.min(100, Math.round(((demoFrictionDuration - demoFrictionRemaining) / demoFrictionDuration) * 100)) : 100}%`,
                            background: 'linear-gradient(90deg, #E07A5F 0%, #749C8F 100%)',
                            transition: 'width 1s linear',
                          }} />
                        </div>

                        {/* Play / Pause / Reset Delay Controls */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '22px' }}>
                          <button
                            onClick={() => setDemoFrictionRunning(!demoFrictionRunning)}
                            style={{
                              background: demoFrictionRunning ? '#F4A261' : 'linear-gradient(135deg, #749C8F 0%, #5E8276 100%)',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px 20px',
                              fontSize: '13px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              minHeight: '44px',
                              boxShadow: '0 2px 8px rgba(94, 130, 118, 0.2)',
                            }}
                          >
                            {demoFrictionRunning ? '⏸️ Pause Delay' : demoFrictionRemaining === 0 ? '✓ Cooldown Complete' : '▶️ Start Delay Countdown'}
                          </button>

                          <button
                            onClick={() => {
                              setDemoFrictionRemaining(demoFrictionDuration);
                              setDemoFrictionRunning(false);
                            }}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#5C524C',
                              border: '1px solid #D1C7BD',
                              borderRadius: '8px',
                              padding: '10px 16px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              minHeight: '44px',
                            }}
                          >
                            ↺ Reset Delay
                          </button>
                        </div>

                        {/* Interactive Values Contract Chips */}
                        <div style={{ textAlign: 'left', backgroundColor: '#FFFFFF', padding: '16px', borderRadius: '12px', border: '1px solid #EAE2D7' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '13px', color: '#2D2826' }}>
                              Core Values Commitment Contract:
                            </strong>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: demoSelectedValues.length > 0 ? '#26594A' : '#DC2626' }}>
                              {demoSelectedValues.length > 0 ? `✓ ${demoSelectedValues.length} Pledged` : '⚠️ At least 1 value required'}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#5C524C', marginBottom: '10px' }}>
                            Select the personal values you are upholding by choosing intentional restraint:
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {DEMO_CORE_VALUES.map((val) => {
                              const isSelected = demoSelectedValues.includes(val);
                              return (
                                <button
                                  key={val}
                                  onClick={() => toggleDemoValue(val)}
                                  style={{
                                    padding: '8px 14px',
                                    minHeight: '40px',
                                    borderRadius: '8px',
                                    border: isSelected ? '1.5px solid #26594A' : '1px solid #D1C7BD',
                                    backgroundColor: isSelected ? '#EBF3F0' : '#FAF8F5',
                                    color: isSelected ? '#1B4332' : '#5C524C',
                                    fontSize: '12px',
                                    fontWeight: isSelected ? 700 : 500,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                  }}
                                >
                                  <span>{isSelected ? '✓' : '+'}</span>
                                  <span>{val}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Block 4: Therapist Audio / Support Lifeline */}
                    {demoActiveBlock === 'support' && (
                      <div style={{ backgroundColor: '#FAF8F5', borderRadius: '16px', padding: '24px', border: '1px solid #EAE2D7' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                          <strong style={{ fontSize: '15px', color: '#2D2826' }}>Therapist Voice Note & Crisis Support Lifeline</strong>
                          <span style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>
                            Simulated Audio & 988 Dialer
                          </span>
                        </div>

                        {/* Audio Player Card */}
                        <div style={{ backgroundColor: '#FFFFFF', padding: '18px', borderRadius: '12px', border: '1px solid #EAE2D7', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '24px' }}>🎙️</span>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#2D2826' }}>Dr. Aris Thorne, Psy.D.</div>
                                <div style={{ fontSize: '11px', color: '#8A8079' }}>Clinical Audio Grounding Note • 0:45</div>
                              </div>
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: '#5C524C' }}>
                              0:{String(demoAudioPlayhead).padStart(2, '0')} / 0:45
                            </div>
                          </div>

                          {/* Playhead Progress Bar */}
                          <div style={{ height: '6px', backgroundColor: '#EAE2D7', borderRadius: '999px', overflow: 'hidden', marginBottom: '14px' }}>
                            <div style={{
                              height: '100%',
                              width: `${(demoAudioPlayhead / 45) * 100}%`,
                              backgroundColor: '#C85A3D',
                              transition: 'width 1s linear',
                            }} />
                          </div>

                          {/* Audio Controls */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => setDemoAudioPlaying(!demoAudioPlaying)}
                                style={{
                                  backgroundColor: '#C85A3D',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '8px 18px',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  minHeight: '44px',
                                }}
                              >
                                {demoAudioPlaying ? '⏸️ Pause Note' : '▶️ Play Voice Memo'}
                              </button>

                              <button
                                onClick={() => setDemoAudioMuted(!demoAudioMuted)}
                                title={demoAudioMuted ? 'Audio is currently muted • Read live captions below' : 'Mute audio'}
                                style={{
                                  backgroundColor: '#FFFFFF',
                                  color: demoAudioMuted ? '#DC2626' : '#5C524C',
                                  border: demoAudioMuted ? '1.5px solid #DC2626' : '1px solid #D1C7BD',
                                  borderRadius: '8px',
                                  padding: '8px 14px',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  minHeight: '44px',
                                }}
                              >
                                {demoAudioMuted ? '🔇 Unmute' : '🔊 Mute'}
                              </button>
                            </div>

                            {demoAudioMuted && (
                              <div style={{
                                backgroundColor: '#FFF8E7',
                                color: '#8A5D00',
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '4px 10px',
                                borderRadius: '6px',
                                border: '1px solid #E0A92C',
                              }}>
                                Audio is currently muted • Read live captions below
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Live Synchronized Subtitle Captions Box */}
                        <div style={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '12px',
                          padding: '16px',
                          border: '1px solid #EAE2D7',
                          marginBottom: '16px',
                          minHeight: '64px',
                        }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#8A8079', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Live Synchronized Subtitles (WCAG AA Accessibility):
                          </div>
                          <div aria-live="polite" style={{ fontSize: '14px', color: '#2D2826', fontStyle: 'italic', lineHeight: '1.4' }}>
                            "{getDemoAudioCaption(demoAudioPlayhead)}"
                          </div>
                        </div>

                        {/* Prominent 1-tap 988 Crisis Lifeline Card */}
                        <div style={{
                          backgroundColor: '#FEF2F2',
                          border: '1.5px solid #EF4444',
                          borderRadius: '12px',
                          padding: '16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '12px',
                        }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                              <strong style={{ fontSize: '14px', color: '#991B1B' }}>988 Suicide & Crisis Lifeline</strong>
                              <span style={{ backgroundColor: '#DC2626', color: '#FFFFFF', fontSize: '10px', fontWeight: 800, padding: '2px 6px', borderRadius: '4px' }}>
                                24/7 FREE
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#7F1D1D' }}>
                              Immediate, confidential support for severe emotional distress or crisis.
                            </div>
                          </div>

                          <a
                            href="tel:988"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              backgroundColor: '#DC2626',
                              color: '#FFFFFF',
                              padding: '12px 20px',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontWeight: 700,
                              fontSize: '13px',
                              minHeight: '48px',
                              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                            }}
                          >
                            📞 Call 988 Lifeline
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Choice Buttons with Anti-Bypass Completion Gating */}
                <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #EAE2D7', display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setDemoCompleted(true)}
                    style={{
                      background: 'linear-gradient(135deg, #749C8F 0%, #5E8276 100%)',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 24px',
                      minHeight: '48px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(94, 130, 118, 0.25)',
                    }}
                  >
                    🌿 I Feel Grounded — Step Back
                  </button>

                  {demoActiveTier === 5 ? (
                    <button
                      disabled
                      style={{
                        backgroundColor: '#64748B',
                        color: '#E2E8F0',
                        border: '1px solid #475569',
                        borderRadius: '10px',
                        padding: '12px 20px',
                        minHeight: '48px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    >
                      🔒 Hard Lock Active (Zero Bypass)
                    </button>
                  ) : (
                    <button
                      disabled={isContinueLocked()}
                      onClick={() => {
                        if (!isContinueLocked()) {
                          setDemoCompleted(true);
                        }
                      }}
                      style={{
                        backgroundColor: isContinueLocked() ? '#F1EBE4' : '#FFFFFF',
                        color: isContinueLocked() ? '#A89F91' : '#5C524C',
                        border: isContinueLocked() ? '1px solid #E0D7CC' : '1.5px solid #749C8F',
                        borderRadius: '10px',
                        padding: '12px 20px',
                        minHeight: '48px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: isContinueLocked() ? 'not-allowed' : 'pointer',
                        opacity: isContinueLocked() ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {demoActiveTier === 1
                        ? (softPauseSeconds > 0 ? `Wait ${softPauseSeconds}s to Continue ➔` : 'Continue to App Mindfully ➔')
                        : demoActiveTier === 2
                        ? ((demoCbtCard === 'reframe' || demoReflectionText.trim().length > 0) ? 'Continue to App Mindfully ➔' : 'Reframe Thought to Continue ➔')
                        : demoActiveTier === 3
                        ? (demoCyclesCompleted >= 1 ? 'Continue to App Mindfully ➔' : 'Complete 1 Breath Cycle to Continue ➔')
                        : (demoFrictionRemaining > 0 ? `Cooldown (${demoFrictionRemaining}s) ➔` : demoSelectedValues.length === 0 ? 'Pledge Value to Continue ➔' : 'Continue to App Mindfully ➔')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Success / Resolution Reflection View */
              <div style={{
                background: 'linear-gradient(135deg, #FFF9F5 0%, #F5EFEB 100%)',
                borderRadius: '16px',
                padding: '28px',
                border: '1px solid #F6D0B8',
                textAlign: 'center',
              }}>
                <span style={{ fontSize: '36px' }}>✨</span>
                <h4 style={{ fontSize: '18px', fontWeight: 800, color: '#2D2826', margin: '12px 0 6px' }}>
                  Mindful Pause Completed!
                </h4>
                <p style={{ fontSize: '14px', color: '#5C524C', maxWidth: '460px', margin: '0 auto 20px', lineHeight: '1.5' }}>
                  You just gave yourself a moment of intentional choice. In clinical studies, taking a 30-second somatic pause reduces compulsive app urges by over <strong>80%</strong>.
                </p>

                {/* Urge Rating Reduction Visualizer */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', backgroundColor: '#FFFFFF', padding: '14px 20px', borderRadius: '12px', maxWidth: '360px', margin: '0 auto 20px', border: '1px solid #EAE2D7' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#8A8079', fontWeight: 600 }}>Initial Urge</div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#DC2626' }}>{urgeRating}/10</div>
                  </div>
                  <span style={{ fontSize: '20px', color: '#F4A261' }}>➔</span>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#8A8079', fontWeight: 600 }}>Post-Pause Urge</div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#5E8276' }}>{postUrgeRating}/10</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <button
                    onClick={() => startDemo()}
                    style={{
                      background: 'linear-gradient(135deg, #E07A5F 0%, #F4A261 100%)',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(224, 122, 95, 0.25)',
                    }}
                  >
                    Try Another Scenario 🔄
                  </button>
                  <button
                    onClick={() => startDemo()}
                    style={{ backgroundColor: '#FFFFFF', color: '#5C524C', border: '1px solid #D1C7BD', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Reset Pause ↺
                  </button>
                </div>
              </div>
            )}
          </div>

        {/* Trust Badges & Highlights */}
        <div style={{
          marginTop: '44px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '32px',
          flexWrap: 'wrap',
          padding: '16px 20px',
          color: '#5C524C',
          fontSize: '13.5px',
          fontWeight: 600,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#E07A5F', fontSize: '16px' }}>✓</span> 80%+ Reduction in Impulsive Urges
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#749C8F', fontSize: '16px' }}>✓</span> 100% On-Device & Zero-Knowledge
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#E07A5F', fontSize: '16px' }}>✓</span> Evidence-Based CBT & Somatic Breath
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#749C8F', fontSize: '16px' }}>✓</span> Available on iOS, Android & Desktop
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" style={{ backgroundColor: '#FFFFFF', padding: '72px 24px', borderTop: '1px solid #EAE2D7' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          <span style={{ color: '#A8442A', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
            Simple & Compassionate
          </span>
          <h2 style={{ fontSize: '34px', fontWeight: 800, color: '#2D2826', marginTop: '8px', letterSpacing: '-0.5px' }}>
            How Luminous Break Works
          </h2>
          <p style={{ color: '#5C524C', fontSize: '16px', maxWidth: '600px', margin: '8px auto 48px', lineHeight: '1.6' }}>
            We replace compulsive digital friction with mindful calm in three effortless steps.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', textAlign: 'left' }}>
            <div style={{ backgroundColor: '#FAF8F5', padding: '28px', borderRadius: '16px', border: '1px solid #EAE2D7' }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>🎯</div>
              <h3 style={{ fontSize: '18px', color: '#2D2826', margin: '0 0 8px 0', fontWeight: 700 }}>1. Choose Your Guardrails</h3>
              <p style={{ fontSize: '14px', color: '#5C524C', lineHeight: '1.6', margin: 0 }}>
                Select the apps and websites you want gentle protection for — like late-night trading, shopping apps, or endless social media feeds.
              </p>
            </div>

            <div style={{ backgroundColor: '#FAF8F5', padding: '28px', borderRadius: '16px', border: '1px solid #EAE2D7' }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>🧘</div>
              <h3 style={{ fontSize: '18px', color: '#2D2826', margin: '0 0 8px 0', fontWeight: 700 }}>2. A Gentle Intercept</h3>
              <p style={{ fontSize: '14px', color: '#5C524C', lineHeight: '1.6', margin: 0 }}>
                When you open a target app, a peaceful breathing screen or reflective prompt appears for a few seconds to break the automatic impulse.
              </p>
            </div>

            <div style={{ backgroundColor: '#FAF8F5', padding: '28px', borderRadius: '16px', border: '1px solid #EAE2D7' }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>📈</div>
              <h3 style={{ fontSize: '18px', color: '#2D2826', margin: '0 0 8px 0', fontWeight: 700 }}>3. Celebrate Your Progress</h3>
              <p style={{ fontSize: '14px', color: '#5C524C', lineHeight: '1.6', margin: 0 }}>
                Watch your mindful habit streak grow over time. See how many urges you've dropped and how much peace of mind you've reclaimed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Multiplatform Ecosystem & Clinician Pairing Architecture */}
      <section id="ecosystem" style={{ backgroundColor: '#FAF8F5', padding: '72px 24px', borderTop: '1px solid #EAE2D7' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: '#26594A', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
              Multiplatform Architecture & Care Delivery
            </span>
            <h2 style={{ fontSize: '34px', fontWeight: 800, color: '#2D2826', marginTop: '8px', letterSpacing: '-0.5px' }}>
              One Ecosystem. Web, iOS, Android & Chrome.
            </h2>
            <p style={{ color: '#5C524C', fontSize: '16px', maxWidth: '720px', margin: '8px auto 0', lineHeight: '1.6' }}>
              Designed as a unified therapeutic companion. Clinicians configure and monitor care on the web; clients receive gentle habit support through their free native mobile apps.
            </p>
          </div>

          {/* 3 Interconnected Platforms */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '28px', marginBottom: '56px' }}>
            
            {/* Platform 1: Web Clinician Portal */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '32px 28px',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>💻</span>
                    <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: '#1B4332' }}>
                      Clinician Web Portal
                    </h3>
                  </div>
                  <span style={{ backgroundColor: '#EBF5F1', color: '#1B4332', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '999px' }}>
                    SaaS Studio
                  </span>
                </div>
                <p style={{ fontSize: '13.5px', color: '#5C524C', lineHeight: '1.6', margin: '0 0 20px' }}>
                  The command center for solo therapists, IOP clinics, and addiction treatment centers. Manage caseloads, craft customized CBT recipes, and execute HIPAA BAAs.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155' }}>
                  <li>✓ <strong>Stripe Self-Service Billing</strong> ($49/mo Solo, $149/mo Clinic)</li>
                  <li>✓ <strong>1-Click QR & 6-Digit PIN Pairing</strong> for clients</li>
                  <li>✓ <strong>Visual Composable Intervention Studio</strong></li>
                  <li>✓ <strong>Zero-Knowledge Envelope Audit Logs</strong></li>
                </ul>
              </div>

              <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #F1F5F9' }}>
                <button
                  onClick={onOpenPortal}
                  style={{
                    width: '100%',
                    backgroundColor: '#1B4332',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  🩺 Access Clinician Portal
                </button>
              </div>
            </div>

            {/* Platform 2: Native Companion Mobile App */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '32px 28px',
              border: '2px solid #F4A261',
              boxShadow: '0 12px 36px rgba(224, 122, 95, 0.12)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '24px',
                backgroundColor: '#E07A5F',
                color: '#FFFFFF',
                padding: '4px 12px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.4px',
              }}>
                FREE COMPANION APP
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>📱</span>
                    <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: '#2D2826' }}>
                      iOS & Android Apps
                    </h3>
                  </div>
                </div>
                <p style={{ fontSize: '13.5px', color: '#5C524C', lineHeight: '1.6', margin: '0 0 20px' }}>
                  Free downloads on Apple App Store & Google Play. Runs natively on your phone to provide on-screen cooling-off pauses when opening habit-triggering apps.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155' }}>
                  <li>✓ <strong>100% Free Companion Download</strong> for all patients</li>
                  <li>✓ <strong>Clinician-Sponsored Unlocks</strong> via QR Handshake</li>
                  <li>✓ <strong>Individual Mindful Plus Login</strong> for web subscribers</li>
                  <li>✓ <strong>Hardware Enclave Security</strong> (keys sealed on-device)</li>
                </ul>
              </div>

              <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #F1F5F9' }}>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#1E293B',
                    color: '#FFFFFF',
                    borderRadius: '10px',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}>
                    <span></span> App Store
                  </div>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#1E293B',
                    color: '#FFFFFF',
                    borderRadius: '10px',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}>
                    <span>▶</span> Google Play
                  </div>
                </div>
              </div>
            </div>

            {/* Platform 3: Desktop Chrome Extension */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '32px 28px',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>🧩</span>
                    <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: '#2D2826' }}>
                      Chrome Extension
                    </h3>
                  </div>
                  <span style={{ backgroundColor: '#F1F5F9', color: '#475569', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '999px' }}>
                    Desktop Browsing
                  </span>
                </div>
                <p style={{ fontSize: '13.5px', color: '#5C524C', lineHeight: '1.6', margin: '0 0 20px' }}>
                  Extends mindful guardrails to your desktop and laptop. Blocks impulsive new tabs and distracting shopping feeds during daytime working hours.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155' }}>
                  <li>✓ <strong>Distraction Intercept Overlays</strong> on target URLs</li>
                  <li>✓ <strong>Box Breathing Mandala</strong> cooling-off periods</li>
                  <li>✓ <strong>Synced Safeguards</strong> with your mobile settings</li>
                  <li>✓ <strong>Free Core Extension</strong> on Chrome Web Store</li>
                </ul>
              </div>

              <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #F1F5F9' }}>
                <div style={{
                  width: '100%',
                  backgroundColor: '#F8FAFC',
                  color: '#475569',
                  border: '1px solid #CBD5E1',
                  borderRadius: '10px',
                  padding: '12px',
                  fontSize: '13px',
                  fontWeight: 700,
                  textAlign: 'center',
                }}>
                  🌐 Available on Chrome Web Store
                </div>
              </div>
            </div>

          </div>

          {/* Clinician Handshake Walkthrough Banner */}
          <div style={{
            background: 'linear-gradient(145deg, #2D2826 0%, #1F1B1A 100%)',
            borderRadius: '24px',
            padding: '40px 36px',
            color: '#FFFFFF',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 16px 40px rgba(0,0,0,0.15)',
          }}>
            <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 36px' }}>
              <span style={{ color: '#F6BD60', fontSize: '12.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
                The Clinician-to-Patient Handshake
              </span>
              <h3 style={{ fontSize: '28px', fontWeight: 800, margin: '8px 0 12px', letterSpacing: '-0.5px' }}>
                How Clinicians Sponsor Patients (Zero Out-of-Pocket Cost to Clients)
              </h3>
              <p style={{ fontSize: '14.5px', color: '#DDD6CF', lineHeight: '1.6', margin: 0 }}>
                Patients never need to enter a credit card. Clinicians subscribe on the web, configure patient care plans, and deliver instant access through a simple, secure QR pairing code.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '16px', padding: '24px 20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>1️⃣</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15.5px', fontWeight: 700, color: '#F6BD60' }}>
                  Clinician Subscribes
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#B3AAA3', lineHeight: '1.5' }}>
                  Therapist or clinic signs up on the web portal via Stripe ($49/mo Solo or $149/mo Treatment Center).
                </p>
              </div>

              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '16px', padding: '24px 20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>2️⃣</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15.5px', fontWeight: 700, color: '#F6BD60' }}>
                  Generate Pairing Code
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#B3AAA3', lineHeight: '1.5' }}>
                  Clinician customizes therapeutic recipes and displays a single-use QR code or 6-digit handshake PIN.
                </p>
              </div>

              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '16px', padding: '24px 20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>3️⃣</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15.5px', fontWeight: 700, color: '#F6BD60' }}>
                  Patient Scans Code
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#B3AAA3', lineHeight: '1.5' }}>
                  Client downloads the free Luminous Break mobile app and scans the code in under 5 seconds.
                </p>
              </div>

              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '16px', padding: '24px 20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>4️⃣</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15.5px', fontWeight: 700, color: '#F6BD60' }}>
                  Full Unlocked Care ($0)
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#B3AAA3', lineHeight: '1.5' }}>
                  All 9 domains and custom clinic recipes unlock on client's phone, 100% sponsored by the provider.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* VISUAL COMPOSABLE BLOCK FEATURE STUDIO & ZERO-KNOWLEDGE PRIVACY           */}
      {/* ========================================================================= */}
      <section id="composable-studio" style={{ backgroundColor: '#FAF8F5', padding: '72px 24px', borderTop: '1px solid #EAE2D7' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: '#26594A', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
              Modular Intervention Architecture
            </span>
            <h2 style={{ fontSize: '34px', fontWeight: 800, color: '#2D2826', marginTop: '8px', letterSpacing: '-0.5px' }}>
              Visual Composable Block Feature Studio
            </h2>
            <p style={{ color: '#5C524C', fontSize: '16px', maxWidth: '680px', margin: '8px auto 0', lineHeight: '1.6' }}>
              Clinicians and users configure 4 clinical-grade building blocks into seamless, trauma-informed digital intercepts. Explore and test each block live below:
            </p>
          </div>

          {/* 4 Composable Building Blocks Grid */}
          <div className="luminous-studio-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '28px', marginBottom: '48px' }}>
            
            {/* 1. Somatic Breath Block */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '28px',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 6px 20px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '24px' }}>🌊</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1B4332' }}>
                      1. Somatic Breath Block
                    </h3>
                  </div>
                  <span style={{ backgroundColor: '#EBF5F1', color: '#1B4332', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '999px' }}>
                    Vagus Nerve Pacer
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#5C524C', lineHeight: '1.5', margin: '0 0 16px' }}>
                  Rhythmic visual wave pacer stimulating parasympathetic relaxation to calm autonomic nervous system arousal.
                </p>

                {/* Cycle Switcher Tabs */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '18px', flexWrap: 'wrap' }}>
                  {[
                    { id: 'box_4_4_4', label: 'Box (4-4-4)' },
                    { id: 'relaxing_4_7_8', label: 'Relaxing (4-7-8)' },
                    { id: 'diaphragmatic_mandala', label: 'Diaphragmatic Wave' },
                  ].map((cycle) => (
                    <button
                      key={cycle.id}
                      onClick={() => {
                        setSomaticCycle(cycle.id as any);
                        setSomaticPhase('Inhale');
                        setSomaticCountdown(cycle.id === 'diaphragmatic_mandala' ? 5 : 4);
                      }}
                      style={{
                        padding: '6px 12px',
                        minHeight: '36px',
                        borderRadius: '6px',
                        border: somaticCycle === cycle.id ? '1.5px solid #1B4332' : '1px solid #CBD5E1',
                        backgroundColor: somaticCycle === cycle.id ? '#EBF5F1' : '#FAF8F5',
                        color: somaticCycle === cycle.id ? '#1B4332' : '#5C524C',
                        fontWeight: somaticCycle === cycle.id ? 700 : 500,
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      {cycle.label}
                    </button>
                  ))}
                </div>

                {/* Live Animated Wave Pacer Circle */}
                <div style={{
                  backgroundColor: '#FAF8F5',
                  borderRadius: '16px',
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px solid #EAE2D7',
                }}>
                  <div style={{
                    width: '110px',
                    height: '110px',
                    borderRadius: '50%',
                    margin: '0 auto 14px',
                    backgroundColor: somaticPhase === 'Inhale' ? '#FFEBDD' : somaticPhase === 'Hold' ? '#FFF7E6' : '#EBF5F1',
                    border: `4px solid ${somaticPhase === 'Inhale' ? '#E07A5F' : somaticPhase === 'Hold' ? '#D9941A' : '#5E8276'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 1s ease-in-out, background-color 0.8s ease',
                    transform: somaticPhase === 'Inhale' ? 'scale(1.12)' : somaticPhase === 'Hold' ? 'scale(1.12)' : 'scale(0.92)',
                    boxShadow: '0 8px 20px rgba(94, 130, 118, 0.2)',
                  }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: somaticPhase === 'Inhale' ? '#9E3C22' : somaticPhase === 'Hold' ? '#7A4D00' : '#204A3C', textTransform: 'uppercase' }}>
                      {somaticPhase}
                    </span>
                    <span style={{ fontSize: '20px', fontWeight: 800, color: somaticPhase === 'Inhale' ? '#9E3C22' : somaticPhase === 'Hold' ? '#7A4D00' : '#204A3C', fontFamily: 'monospace' }}>
                      00:0{somaticCountdown}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#5C524C', fontStyle: 'italic' }}>
                    {somaticPhase === 'Inhale' && 'Slow gentle inhalation through the nose into lower belly'}
                    {somaticPhase === 'Hold' && 'Soft breath retention without shoulder tension'}
                    {somaticPhase === 'Exhale' && 'Smooth, elongated exhalation engaging vagus nerve'}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: '16px', fontSize: '11px', color: '#26594A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>✓</span> Clinically demonstrated to reduce acute digital cravings within 3 cycles.
              </div>
            </div>

            {/* 2. Cognitive Reframe Block */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '28px',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 6px 20px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '24px' }}>🧠</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1B4332' }}>
                      2. Cognitive Reframe Block
                    </h3>
                  </div>
                  <span style={{ backgroundColor: '#E0F2FE', color: '#0369A1', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '999px' }}>
                    CBT & Math Curves
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#5C524C', lineHeight: '1.5', margin: '0 0 16px' }}>
                  Cognitive inquiry cards, urge surfing scales, and empirical loss curve probabilities.
                </p>

                {/* CBT Thought vs Reframe Card */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <button
                    onClick={() => setActiveCbtCard('thought')}
                    style={{
                      flex: 1,
                      padding: '6px',
                      minHeight: '34px',
                      borderRadius: '6px',
                      border: activeCbtCard === 'thought' ? '1.5px solid #DC2626' : '1px solid #CBD5E1',
                      backgroundColor: activeCbtCard === 'thought' ? '#FEE2E2' : '#FAF8F5',
                      color: activeCbtCard === 'thought' ? '#DC2626' : '#5C524C',
                      fontSize: '11px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Automatic Thought
                  </button>
                  <button
                    onClick={() => setActiveCbtCard('reframe')}
                    style={{
                      flex: 1,
                      padding: '6px',
                      minHeight: '34px',
                      borderRadius: '6px',
                      border: activeCbtCard === 'reframe' ? '1.5px solid #16A34A' : '1px solid #CBD5E1',
                      backgroundColor: activeCbtCard === 'reframe' ? '#E8F5E9' : '#FAF8F5',
                      color: activeCbtCard === 'reframe' ? '#16A34A' : '#5C524C',
                      fontSize: '11px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Rational Reframe
                  </button>
                </div>

                <div style={{
                  backgroundColor: activeCbtCard === 'thought' ? '#FFF5F5' : '#F4FBF7',
                  border: `1px solid ${activeCbtCard === 'thought' ? '#FED7D7' : '#C6F6D5'}`,
                  borderRadius: '12px',
                  padding: '14px',
                  fontSize: '13px',
                  color: '#2D2826',
                  lineHeight: '1.5',
                  marginBottom: '14px',
                }}>
                  {activeCbtCard === 'thought' ? (
                    <span><strong>Impulse:</strong> "I must open this trading chart / feed right now or I will miss a once-in-a-lifetime opportunity!"</span>
                  ) : (
                    <span><strong>Grounded Truth:</strong> "Urgency is a dopamine illusion. 83% of speculative momentum trades yield net losses. Stepping back preserves capital."</span>
                  )}
                </div>

                {/* Craving vs Need Slider (1-10) */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, color: '#2D2826', marginBottom: '4px' }}>
                    <span>Craving vs. Need Intensity</span>
                    <span style={{ color: cravingSliderVal >= 8 ? '#DC2626' : cravingSliderVal >= 5 ? '#D97706' : '#16A34A' }}>
                      {cravingSliderVal} / 10
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={cravingSliderVal}
                    onChange={(e) => setCravingSliderVal(Number(e.target.value))}
                    aria-label="Craving vs Need Intensity Slider"
                    style={{ width: '100%', cursor: 'pointer', accentColor: '#E07A5F' }}
                  />
                  <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
                    {cravingSliderVal <= 3 && 'Level 1-3: Mild Habitual Pull • Easily redirected with 1 breath.'}
                    {cravingSliderVal >= 4 && cravingSliderVal <= 7 && 'Level 4-7: Moderate Urge Wave • Urge surfing recommended; wave peaks in 90s.'}
                    {cravingSliderVal >= 8 && 'Level 8-10: Acute Dopamine Surge • Requires somatic pause & cooling lock.'}
                  </div>
                </div>

                {/* Mathematical Loss Probability Visualizer */}
                <div style={{ backgroundColor: '#F8FAFC', padding: '10px 14px', borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '11px', color: '#334155' }}>
                  📉 <strong>Loss Probability Curve:</strong> Speculative trades have an <strong>-83% net return</strong> over 90 days vs <strong>+12%</strong> compounded mindful savings.
                </div>
              </div>
              <div style={{ marginTop: '16px', fontSize: '11px', color: '#26594A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>✓</span> Disrupts cognitive distortions and cognitive bias in real-time.
              </div>
            </div>

            {/* 3. Intentional Friction Block */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '28px',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 6px 20px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '24px' }}>⏳</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1B4332' }}>
                      3. Intentional Friction Block
                    </h3>
                  </div>
                  <span style={{ backgroundColor: '#FEF3C7', color: '#B45309', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '999px' }}>
                    Time-Locks & Values
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#5C524C', lineHeight: '1.5', margin: '0 0 16px' }}>
                  Enforces cooling-off commitment delays and personal values reflection contracts.
                </p>

                {/* Delay Duration Presets */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                  {[30, 60, 120].map((sec) => (
                    <button
                      key={sec}
                      onClick={() => {
                        setFrictionDuration(sec);
                        setFrictionCountdown(sec);
                        setIsFrictionRunning(false);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px',
                        minHeight: '38px',
                        borderRadius: '8px',
                        border: frictionDuration === sec ? '1.5px solid #B45309' : '1px solid #CBD5E1',
                        backgroundColor: frictionDuration === sec ? '#FEF3C7' : '#FAF8F5',
                        color: frictionDuration === sec ? '#92400E' : '#5C524C',
                        fontWeight: frictionDuration === sec ? 700 : 500,
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      {sec}s Delay
                    </button>
                  ))}
                </div>

                {/* Interactive Countdown Bar */}
                <div style={{ backgroundColor: '#FAF8F5', padding: '16px', borderRadius: '12px', border: '1px solid #EAE2D7', marginBottom: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#2D2826' }}>Cooling-Off Delay</span>
                    <span style={{ fontSize: '16px', fontWeight: 800, color: '#B45309', fontFamily: 'monospace' }}>
                      00:{frictionCountdown < 10 ? `0${frictionCountdown}` : frictionCountdown}
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '8px', backgroundColor: '#E2E8F0', borderRadius: '999px', overflow: 'hidden', marginBottom: '12px' }}>
                    <div style={{
                      width: `${(frictionCountdown / frictionDuration) * 100}%`,
                      height: '100%',
                      backgroundColor: '#D97706',
                      transition: 'width 1s linear',
                    }} />
                  </div>
                  <button
                    onClick={() => setIsFrictionRunning(!isFrictionRunning)}
                    style={{
                      backgroundColor: isFrictionRunning ? '#EF4444' : '#D97706',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {isFrictionRunning ? '⏸️ Pause Timer' : '▶️ Test Cooling Delay'}
                  </button>
                </div>

                {/* Core Values Selector Chips */}
                <div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#2D2826', display: 'block', marginBottom: '6px' }}>
                    Personal Values Reflection Contract:
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {['Restorative Sleep', 'Financial Freedom', 'Family & Loved Ones', 'Mental Clarity', 'Physical Health'].map((val) => {
                      const isSelected = selectedValues.includes(val);
                      return (
                        <button
                          key={val}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedValues(selectedValues.filter((v) => v !== val));
                            } else {
                              setSelectedValues([...selectedValues, val]);
                            }
                          }}
                          style={{
                            padding: '4px 10px',
                            minHeight: '30px',
                            borderRadius: '999px',
                            border: isSelected ? '1.5px solid #16A34A' : '1px solid #CBD5E1',
                            backgroundColor: isSelected ? '#E8F5E9' : '#FAF8F5',
                            color: isSelected ? '#16A34A' : '#64748B',
                            fontSize: '11px',
                            fontWeight: isSelected ? 700 : 500,
                            cursor: 'pointer',
                          }}
                        >
                          {isSelected ? '✓ ' : '+ '}{val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '16px', fontSize: '11px', color: '#26594A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>✓</span> Prevents impulsive bypass by mandating deliberate cooling-off time.
              </div>
            </div>

            {/* 4. Support Block */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '28px',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 6px 20px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '24px' }}>🎙️</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1B4332' }}>
                      4. Support Block
                    </h3>
                  </div>
                  <span style={{ backgroundColor: '#FEE2E2', color: '#DC2626', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '999px' }}>
                    Voice Memos & 988
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#5C524C', lineHeight: '1.5', margin: '0 0 16px' }}>
                  Clinician voice/video notes with live synchronized captions and direct 988 emergency lifeline.
                </p>

                {/* Voice Memo Player Preview */}
                <div style={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: '14px',
                  border: '1px solid #E2E8F0',
                  marginBottom: '14px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B' }}>Mindful Guide Grounding Memo</div>
                      <div style={{ fontSize: '10px', color: '#64748B' }}>Therapist Supportive Voice Memo • 0:{supportPlayhead < 10 ? `0${supportPlayhead}` : supportPlayhead} / 0:45</div>
                    </div>
                    <button
                      onClick={() => setIsPlayingSupportAudio(!isPlayingSupportAudio)}
                      style={{
                        backgroundColor: isPlayingSupportAudio ? '#EF4444' : '#16A34A',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {isPlayingSupportAudio ? '⏹️ Stop' : '▶️ Play Note'}
                    </button>
                  </div>

                  {/* Synchronized Captions Subtitle Box */}
                  <div style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    border: '1px solid #E2E8F0',
                    fontSize: '11.5px',
                    color: '#334155',
                    fontStyle: 'italic',
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {supportPlayhead <= 12 && '"[Mindful Guide]: Take a gentle breath in through your nose. Notice what your body actually needs right now..."'}
                    {supportPlayhead > 12 && supportPlayhead <= 28 && '"[Mindful Guide]: The impulse you feel is only a chemical signal. You do not have to act on it immediately..."'}
                    {supportPlayhead > 28 && '"[Mindful Guide]: You have made so much progress. Let this urge pass safely and return to your day."'}
                  </div>
                  <div style={{ fontSize: '10px', color: '#64748B', marginTop: '4px' }}>
                    🔊 Volume: 85% • Read live captions in quiet environments
                  </div>
                </div>

                {/* 988 Suicide & Crisis Lifeline Dialer Card */}
                <div style={{
                  backgroundColor: '#FFF5F5',
                  border: '1.5px solid #FEB2B2',
                  borderRadius: '12px',
                  padding: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#991B1B', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🆘</span> 988 Suicide & Crisis Lifeline
                    </div>
                    <div style={{ fontSize: '11px', color: '#7F1D1D', marginTop: '2px' }}>
                      Free, confidential, 24/7 mental health crisis support by phone or text.
                    </div>
                  </div>
                  <a
                    href="tel:988"
                    style={{
                      minWidth: '48px',
                      minHeight: '48px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#DC2626',
                      color: '#FFFFFF',
                      textDecoration: 'none',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 700,
                      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    📞 Call 988
                  </a>
                </div>
              </div>
              <div style={{ marginTop: '16px', fontSize: '11px', color: '#26594A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>✓</span> Provides human connection and crisis safety rails in moments of acute distress.
              </div>
            </div>

          </div>

          {/* ========================================================================= */}
          {/* VISUAL ZERO-KNOWLEDGE PRIVACY GUARANTEE                                   */}
          {/* ========================================================================= */}
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '24px',
            padding: '36px',
            color: '#FFFFFF',
            boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
            border: '1px solid #334155',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <span style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981', fontSize: '11px', fontWeight: 800, padding: '4px 14px', borderRadius: '999px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                Zero-Knowledge Privacy Architecture
              </span>
              <h3 style={{ fontSize: '26px', fontWeight: 800, color: '#FFFFFF', margin: '10px 0 6px' }}>
                How Your Privacy Is Cryptographically Guaranteed
              </h3>
              <p style={{ fontSize: '14px', color: '#94A3B8', maxWidth: '640px', margin: '0 auto', lineHeight: '1.6' }}>
                We engineer privacy into the mathematics. Your browsing activity never leaves your physical device unencrypted.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📱</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15px', color: '#F1F5F9', fontWeight: 700 }}>1. On-Device Local Shield</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#94A3B8', lineHeight: '1.5' }}>
                  Web extension and mobile app engines evaluate targets 100% locally. <strong>0 raw URLs</strong> and 0 keystrokes are ever sent to any server.
                </p>
              </div>

              <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔐</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15px', color: '#F1F5F9', fontWeight: 700 }}>2. Client-Side Encryption</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#94A3B8', lineHeight: '1.5' }}>
                  Multi-recipient envelope encryption using <strong>AES-256-GCM</strong> wrapped with clinician <strong>RSA-OAEP-2048</strong> keys.
                </p>
              </div>

              <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>⚡</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15px', color: '#F1F5F9', fontWeight: 700 }}>3. Volatile RAM Decryption</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#94A3B8', lineHeight: '1.5' }}>
                  Decryption keys reside strictly inside clinician browser RAM. Zero persistent cloud keys and instant zeroization upon session close.
                </p>
              </div>

              <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>🛡️</div>
                <h4 style={{ margin: '0 0 6px', fontSize: '15px', color: '#F1F5F9', fontWeight: 700 }}>4. Client-Controlled Data ACLs</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#94A3B8', lineHeight: '1.5' }}>
                  Clients control data sharing granularity (Full Metadata, Summary Only, or Completely Isolated) with instant 1-tap disconnection.
                </p>
              </div>
            </div>

            <div style={{ marginTop: '24px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '18px' }}>
              <span style={{ fontSize: '12px', color: '#10B981', fontWeight: 700 }}>
                🛡️ Verified Zero-Knowledge: 0 URLs Logged • Hardware-Backed Keystore • HIPAA § 164.312 Compliant
              </span>
            </div>
          </div>

        </div>
      </section>

      {/* 9 Behavioral Wellness Domains */}
      <section id="wellness-pillars" style={{ backgroundColor: '#FAF8F5', padding: '72px 24px', borderTop: '1px solid #EAE2D7' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: '#26594A', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
              Tailored Micro-Interventions
            </span>
            <h2 style={{ fontSize: '34px', fontWeight: 800, color: '#2D2826', marginTop: '8px', letterSpacing: '-0.5px' }}>
              The 9 Behavioral Guardrail Areas
            </h2>
            <p style={{ color: '#5C524C', fontSize: '16px', maxWidth: '650px', margin: '8px auto 0', lineHeight: '1.6' }}>
              Grounded in proven cognitive-behavioral principles and somatic grounding to help you stay present and intentional.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            {[
              { icon: '📈', title: 'Financial & Speculation', desc: 'Urge surfing timers, loss curve perspectives, and cooling-off contracts for day trading, crypto, and betting apps.' },
              { icon: '📱', title: 'Social Media & Infinite Feeds', desc: 'Reflective thought-reframing prompts and emotional check-ins to gently interrupt TikTok, Reels, and doomscrolling.' },
              { icon: '🛍️', title: 'Impulse Shopping & Flash Sales', desc: 'Wage-to-hour value check-ins and cooling-off pauses on late-night checkout carts before impulsive buys.' },
              { icon: '🥗', title: 'Late-Night Food Delivery', desc: 'Somatic guided breathing animations and hunger check-ins on DoorDash, Uber Eats, and binge delivery apps.' },
              { icon: '🍷', title: 'Substance & Alcohol Reduction', desc: 'Supportive audio reminders from loved ones and direct single-tap crisis and supportive contact resources.' },
              { icon: '⏳', title: 'Screen Time & Sleep Boundaries', desc: 'Physical stretching prompts and automatic heightened strictness during late-night sleep hours.' },
              { icon: '🔞', title: 'Adult Media & Explicit Content', desc: 'Mindful urge acceptance, dopamine cycle redirection, and private local-only guardrail protection.' },
              { icon: '🎮', title: 'Gaming & In-Game Loot', desc: 'Delayed gratification cooldowns and budget boundaries on Steam, Roblox, and in-game microtransactions.' },
              { icon: '💼', title: 'After-Hours Work & Burnout', desc: 'Gentle work-life boundary pauses on Slack, Work Gmail, and Jira after 7:00 PM to protect recovery.' },
            ].map((pillar, idx) => (
              <div key={idx} style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                padding: '28px',
                border: '1px solid #EAE2D7',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                boxShadow: '0 4px 14px rgba(94, 75, 66, 0.04)',
              }}>
                <div style={{ fontSize: '32px' }}>{pillar.icon}</div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#2D2826' }}>{pillar.title}</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#5C524C', lineHeight: '1.55' }}>{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing & Free vs. Paid */}
      <section id="pricing" style={{ backgroundColor: '#FFFFFF', padding: '72px 24px', borderTop: '1px solid #EAE2D7' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <span style={{ color: '#A8442A', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
              Simple & Transparent
            </span>
            <h2 style={{ fontSize: '34px', fontWeight: 800, color: '#2D2826', marginTop: '8px', letterSpacing: '-0.5px' }}>
              Plans for Everyone
            </h2>
            <p style={{ color: '#5C524C', fontSize: '16px', maxWidth: '650px', margin: '8px auto 20px', lineHeight: '1.6' }}>
              Free core tier included. Plus, if your therapist uses Luminous Break, your full premium access is 100% sponsored.
            </p>

            {/* Monthly / Annual Toggle */}
            <div style={{ display: 'inline-flex', backgroundColor: '#F3EDE4', padding: '4px', borderRadius: '999px', border: '1px solid #EAE2D7' }}>
              <button
                onClick={() => setBillingCycle('monthly')}
                style={{
                  padding: '8px 22px',
                  borderRadius: '999px',
                  border: 'none',
                  background: billingCycle === 'monthly' ? 'linear-gradient(135deg, #E07A5F 0%, #F4A261 100%)' : 'transparent',
                  color: billingCycle === 'monthly' ? '#FFFFFF' : '#6B605B',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: billingCycle === 'monthly' ? '0 2px 8px rgba(224, 122, 95, 0.25)' : 'none',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                style={{
                  padding: '8px 22px',
                  borderRadius: '999px',
                  border: 'none',
                  background: billingCycle === 'annual' ? 'linear-gradient(135deg, #E07A5F 0%, #F4A261 100%)' : 'transparent',
                  color: billingCycle === 'annual' ? '#FFFFFF' : '#6B605B',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: billingCycle === 'annual' ? '0 2px 8px rgba(224, 122, 95, 0.25)' : 'none',
                }}
              >
                Annual (Save 38%) 🌅
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(265px, 1fr))', gap: '24px', alignItems: 'stretch' }}>
            {/* Tier 1: Free Starter */}
            <div style={{
              backgroundColor: '#FAF8F5',
              borderRadius: '20px',
              padding: '36px 24px',
              border: '1px solid #EAE2D7',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ color: '#26594A', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  🌱 Free Starter
                </div>
                <div style={{ fontSize: '36px', fontWeight: 800, color: '#2D2826', margin: '12px 0 6px' }}>
                  $0 <span style={{ fontSize: '16px', fontWeight: 500, color: '#6B605B' }}>/ Free Tier</span>
                </div>
                <p style={{ fontSize: '13px', color: '#6B605B', margin: '0 0 24px 0', lineHeight: '1.5' }}>
                  Essential cooling-off breathing tools for individual self-regulation, with 100% free self-serve iOS access to all 9 domains ($0, no paywalls).
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: '#4A413C' }}>
                  <li>✓ <strong>All 9 Domains Free on iOS ($0 Self-Serve Companion)</strong></li>
                  <li>✓ Guided 16s Animated Box Breathing Mandala</li>
                  <li>✓ Chrome Web Extension integration</li>
                  <li>✓ Local Zero-Knowledge storage & Privacy</li>
                  <li>✓ <strong>Free Client Pairing</strong> with a therapist</li>
                </ul>
              </div>

              <button style={{
                marginTop: '32px',
                backgroundColor: '#FFFFFF',
                color: '#26594A',
                border: '2px solid #26594A',
                borderRadius: '10px',
                padding: '12px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
              }}>
                Download Free App
              </button>
            </div>

            {/* Tier 2: Plus Individual */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '36px 24px',
              border: '2px solid #F4A261',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 12px 36px rgba(224, 122, 95, 0.16)',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '24px',
                background: 'linear-gradient(135deg, #E07A5F 0%, #F4A261 100%)',
                color: '#FFFFFF',
                padding: '4px 14px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.4px',
              }}>
                POPULAR
              </div>

              <div>
                <div style={{ color: '#A8442A', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  ⭐ Mindful Plus (Web & Desktop Extension)
                </div>
                <div style={{ fontSize: '36px', fontWeight: 800, color: '#2D2826', margin: '12px 0 6px' }}>
                  {billingCycle === 'annual' ? '$4.99' : '$7.99'} <span style={{ fontSize: '16px', fontWeight: 500, color: '#6B605B' }}>/ month</span>
                </div>
                <p style={{ fontSize: '13px', color: '#6B605B', margin: '0 0 24px 0', lineHeight: '1.5' }}>
                  Desktop extension multi-device sync and clinician portal tools. (iOS app includes 100% free self-serve access to all 9 domains).
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: '#4A413C' }}>
                  <li>✓ <strong>Desktop Browser Extension Sync & Web Vault</strong></li>
                  <li>✓ <strong>Nighttime Sleep Guardrails & Custom Triggers</strong></li>
                  <li>✓ <strong>Smart On-Device Habit Discovery</strong></li>
                  <li>✓ Custom App & Website additions</li>
                  <li>✓ iOS companion is 100% free with all 9 domains included ($0)</li>
                </ul>
              </div>

              <button
                onClick={() => {
                  setCheckoutPlan({
                    id: 'mindful_plus_trial',
                    name: 'Luminous Break — Mindful Plus (14-Day Free Trial)',
                    price: billingCycle === 'annual' ? '$4.99' : '$7.99',
                    billingInterval: billingCycle === 'annual' ? 'year' : 'month',
                    description: '14-Day Free Trial. All 9 Habit Guardrails, Sleep Protection, and Smart Suggestions.',
                  });
                  setShowCheckoutModal(true);
                }}
                style={{
                  marginTop: '32px',
                  background: 'linear-gradient(135deg, #E07A5F 0%, #F4A261 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '13px',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(224, 122, 95, 0.35)',
                }}
              >
                Start 14-Day Free Trial
              </button>
            </div>

            {/* Tier 3: Solo Practitioner */}
            <div style={{
              background: 'linear-gradient(145deg, #2D2826 0%, #1F1B1A 100%)',
              borderRadius: '20px',
              padding: '36px 24px',
              color: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <div>
                <div style={{ color: '#F6BD60', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  🩺 Solo Practitioner
                </div>
                <div style={{ fontSize: '36px', fontWeight: 800, color: '#FFFFFF', margin: '12px 0 6px' }}>
                  {billingCycle === 'annual' ? '$39' : '$49'} <span style={{ fontSize: '16px', fontWeight: 500, color: '#DDD6CF' }}>/ month</span>
                </div>
                <p style={{ fontSize: '13px', color: '#DDD6CF', margin: '0 0 24px 0', lineHeight: '1.5' }}>
                  Specialized clinical intervention studio for solo therapists treating acute cases.
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: '#EAE2D7' }}>
                  <li>✓ <strong>1 Clinician Seat</strong></li>
                  <li>✓ <strong>Up to 5 Active Sponsored Patients</strong></li>
                  <li>✓ <strong>Instant QR & 6-Digit PIN Pairing</strong></li>
                  <li>✓ Custom CBT & Mindfulness Recipe Studio</li>
                  <li>✓ Therapist Voice & Video Guidance Notes</li>
                  <li>✓ <em>Add-on: +5 Patients for $20/mo</em></li>
                </ul>
              </div>

              <button
                onClick={() => {
                  setCheckoutPlan({
                    id: 'solo_practitioner_plan',
                    name: 'Luminous Break — Solo Practitioner Suite',
                    price: billingCycle === 'annual' ? '$39' : '$49',
                    billingInterval: billingCycle === 'annual' ? 'year' : 'month',
                    description: '1 Clinician seat, up to 5 sponsored patients included, full CBT intervention studio.',
                  });
                  setShowCheckoutModal(true);
                }}
                style={{
                  marginTop: '32px',
                  backgroundColor: '#FAF8F5',
                  color: '#2D2826',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '13px',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }}
              >
                Start Practitioner Plan
              </button>
            </div>

            {/* Tier 4: Outpatient Clinic & Treatment Center */}
            <div style={{
              background: 'linear-gradient(145deg, #1B4332 0%, #081C15 100%)',
              borderRadius: '20px',
              padding: '36px 24px',
              color: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 12px 36px rgba(27, 67, 50, 0.35)',
              border: '2px solid #52B788',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '20px',
                backgroundColor: '#52B788',
                color: '#081C15',
                padding: '4px 12px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.4px',
              }}>
                TREATMENT CENTERS
              </div>

              <div>
                <div style={{ color: '#95D5B2', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  🏥 Treatment Center Starter
                </div>
                <div style={{ fontSize: '36px', fontWeight: 800, color: '#FFFFFF', margin: '12px 0 6px' }}>
                  {billingCycle === 'annual' ? '$119' : '$149'} <span style={{ fontSize: '16px', fontWeight: 500, color: '#B7E4C7' }}>/ month</span>
                </div>
                <p style={{ fontSize: '13px', color: '#D8F3DC', margin: '0 0 24px 0', lineHeight: '1.5' }}>
                  Designed for IOPs, addiction programs, and multidisciplinary clinical teams.
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: '#E9F5ED' }}>
                  <li>✓ <strong>5 Clinician Seats Included</strong></li>
                  <li>✓ <strong>Up to 30 Active Sponsored Patients</strong></li>
                  <li>✓ <strong>Verified Clinic Domain</strong> (@yourclinic.org)</li>
                  <li>✓ <strong>Signed HIPAA BAA Agreement</strong></li>
                  <li>✓ Multi-Clinician Care Circle Team Sharing</li>
                  <li>✓ <em>Add-ons: +$25/mo per clinician, +$20/mo per +5 patients</em></li>
                </ul>
              </div>

              <button
                onClick={() => {
                  setCheckoutPlan({
                    id: 'outpatient_clinic_plan',
                    name: 'Luminous Break — Outpatient Clinic Starter (Treatment Center)',
                    price: billingCycle === 'annual' ? '$119' : '$149',
                    billingInterval: billingCycle === 'annual' ? 'year' : 'month',
                    description: '5 Clinician seats, 30 sponsored patients, Verified Clinic Domain, and signed HIPAA BAA.',
                  });
                  setShowCheckoutModal(true);
                }}
                style={{
                  marginTop: '32px',
                  backgroundColor: '#52B788',
                  color: '#081C15',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '13px',
                  fontWeight: 800,
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(82, 183, 136, 0.4)',
                }}
              >
                Start Treatment Center Plan
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* FREQUENTLY ASKED QUESTIONS (FAQ)                                          */}
      {/* ========================================================================= */}
      <section id="faq" style={{ backgroundColor: '#FAF8F5', padding: '72px 24px', borderTop: '1px solid #EAE2D7' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: '#A8442A', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
              Got Questions?
            </span>
            <h2 style={{ fontSize: '34px', fontWeight: 800, color: '#2D2826', marginTop: '8px', letterSpacing: '-0.5px' }}>
              Frequently Asked Questions
            </h2>
            <p style={{ color: '#5C524C', fontSize: '16px', maxWidth: '650px', margin: '8px auto 0', lineHeight: '1.6' }}>
              Everything you need to know about plans, mobile app downloads, clinician sponsorship, and zero-knowledge privacy.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            
            {/* Q1 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px 28px', border: '1px solid #EAE2D7', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#2D2826' }}>
                💳 Do patients have to pay for the mobile app?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#5C524C', lineHeight: '1.6' }}>
                <strong>No, never.</strong> The Luminous Break mobile app is 100% free to download on both the Apple App Store and Google Play Store. When you are under the care of a participating therapist, psychiatrist, or addiction treatment clinic, your entire access—including all 9 habit domains and custom clinician-authored intervention recipes—is fully sponsored by your healthcare provider at zero out-of-pocket cost to you.
              </p>
            </div>

            {/* Q2 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px 28px', border: '1px solid #EAE2D7', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#2D2826' }}>
                🩺 How does clinician or treatment center sponsorship work?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#5C524C', lineHeight: '1.6' }}>
                Practitioners and clinical teams subscribe to our <strong>Solo Practitioner</strong> ($49/mo) or <strong>Treatment Center Starter</strong> ($149/mo) plan on our website. In their Coach Studio, they configure therapeutic recipes and click "Pair Patient" to display a one-time QR code or 6-digit handshake PIN. You open your free mobile app, tap "Pair with Therapist", and scan the code. Your phone registers your provider's public key in hardware enclave storage within seconds.
              </p>
            </div>

            {/* Q3: What Stays on Device vs What Clinician Sees */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px 28px', border: '1px solid #EAE2D7', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: '17px', fontWeight: 700, color: '#2D2826' }}>
                🔒 What information stays on my phone, and what does my therapist actually see?
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#5C524C', lineHeight: '1.6' }}>
                Luminous Break is built on the principle of strict on-device privacy. You can build mindful habits and pause impulses with total confidence that nobody is watching your personal screen activity, browsing history, or private thoughts.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                {/* Left Card: Stays On Phone */}
                <div style={{ backgroundColor: '#FDF8F6', borderRadius: '12px', padding: '16px 18px', border: '1px solid #F2DDD7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📱</span>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#A8442A' }}>
                      Stays Strictly On Your Device (Never Shared)
                    </h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#5C524C', lineHeight: '1.6' }}>
                    <li><strong>Websites & URLs:</strong> Specific URLs, store items, and video links are analyzed strictly on your device and are never sent anywhere.</li>
                    <li><strong>Searches & Keystrokes:</strong> Zero keystroke logging, text tracking, or search history capture.</li>
                    <li><strong>Apps & Screen History:</strong> Specific app names and daily screen usage stay locked on your phone.</li>
                    <li><strong>Private Journal Reflections:</strong> Personal writing stays in your hardware enclave vault.</li>
                  </ul>
                </div>

                {/* Right Card: What Therapist Sees */}
                <div style={{ backgroundColor: '#F4F9F6', borderRadius: '12px', padding: '16px 18px', border: '1px solid #D1E7DD' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '16px' }}>🩺</span>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1B4332' }}>
                      What Your Therapist Sees (When Paired)
                    </h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#5C524C', lineHeight: '1.6' }}>
                    <li><strong>Mindful Pauses:</strong> Date and time a pause was completed or stepped back from.</li>
                    <li><strong>General Wellness Area:</strong> Broad focus category (e.g. <em>Late-Night Screen Time</em> or <em>Mindful Spending</em>).</li>
                    <li><strong>Urge Shift Ratings:</strong> How your urge rating shifted before and after the exercise (e.g. 7/10 down to 2/10).</li>
                    <li><strong>Therapist Audio Notes:</strong> Whether you completed listening to an encouraging audio memo from your therapist.</li>
                  </ul>
                </div>
              </div>

              <div style={{ backgroundColor: '#FAF8F5', borderRadius: '10px', padding: '10px 14px', border: '1px solid #EAE2D7', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '15px' }}>🛡️</span>
                <p style={{ margin: 0, fontSize: '12.5px', color: '#665E58', lineHeight: '1.5' }}>
                  <strong>Client-controlled sharing:</strong> Pairing with a therapist is 100% voluntary. You can switch to Completely Isolated mode or disconnect anytime with a single tap in your app settings.
                </p>
              </div>
            </div>

            {/* Q4 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px 28px', border: '1px solid #EAE2D7', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#2D2826' }}>
                ⭐ Can I use the app as an individual without a therapist?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#5C524C', lineHeight: '1.6' }}>
                Yes! The iOS app includes 100% free self-serve access to all 9 domains and pause interventions ($0, no paywalls). Anyone can download the iOS app and configure guardrails with complete zero-knowledge privacy. Web subscriptions like Mindful Plus ($7.99/mo or $4.99/mo annual) are specifically for desktop extension sync and clinician portal tools.
              </p>
            </div>

            {/* Q5 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px 28px', border: '1px solid #EAE2D7', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#2D2826' }}>
                📱 How do I connect my website subscription to my phone or browser?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#5C524C', lineHeight: '1.6' }}>
                The iOS mobile app is 100% free for all 9 domains without requiring any subscription. If your clinician sponsors your care or you use the desktop web extension, simply sign in with your email address to sync clinician recipes and desktop guardrails across your devices.
              </p>
            </div>

            {/* Q6 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px 28px', border: '1px solid #EAE2D7' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#2D2826' }}>
                🔄 How do cancellations and refunds work?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#5C524C', lineHeight: '1.6' }}>
                We believe in ethical, hassle-free subscriptions. You can cancel anytime with 1-click self-service directly inside the Coach Portal under <em>Organization & Team ➔ Billing</em>. You will retain full access until the end of your prepaid period. Additionally, direct web subscriptions and clinic plans include a <strong>30-day 100% money-back guarantee</strong>—if you are not completely satisfied within 30 days, email <a href="mailto:billing@luminousbreak.com" style={{ color: '#E07A5F', fontWeight: 600 }}>billing@luminousbreak.com</a> for an immediate, full refund.
              </p>
            </div>

            {/* Q7 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px 28px', border: '1px solid #EAE2D7' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#2D2826' }}>
                🛡️ Is my behavioral data private from tech platforms and insurance companies?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#5C524C', lineHeight: '1.6' }}>
                <strong>100% private.</strong> Luminous Break is built on Zero-Knowledge Envelope Encryption. Your habit triggers, screen time patterns, and journal reflections are processed and stored solely on your device's hardware enclave. When paired with a therapist, only mutually consented clinical indicators are encrypted using your therapist's RSA-4096 public key. We never sell data, serve advertisements, or monetize your digital wellness.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Our Privacy Promise */}
      <section id="privacy-promise" style={{ maxWidth: '1200px', margin: '0 auto', padding: '72px 24px' }}>
        <div style={{
          background: 'linear-gradient(145deg, #2D2826 0%, #1F1B1A 100%)',
          borderRadius: '24px',
          padding: '48px 40px',
          color: '#FFFFFF',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '40px',
          alignItems: 'center',
          boxShadow: '0 16px 40px rgba(0,0,0,0.14)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <div>
            <span style={{ color: '#F6BD60', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
              Our Privacy Promise
            </span>
            <h2 style={{ fontSize: '32px', fontWeight: 800, color: '#FFFFFF', margin: '12px 0 16px', letterSpacing: '-0.5px' }}>
              Your Habits Stay on Your Device. Period.
            </h2>
            <p style={{ color: '#E2DAD2', fontSize: '15px', lineHeight: '1.65' }}>
              Unlike invasive activity trackers or ad-driven apps, Luminous Break runs locally on your personal phone. We never sell your data, track your browsing history, or monitor your private activity.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.12)' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#98C1B3' }}>📱 100% On-Device Protection</div>
              <div style={{ fontSize: '12px', color: '#DDD6CF', marginTop: '4px', lineHeight: '1.5' }}>Guardrails and mindful pause checks happen instantly on your device without sending your browsing history anywhere.</div>
            </div>

            <div style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.12)' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#98C1B3' }}>🚫 Zero Ads & Zero Data Brokers</div>
              <div style={{ fontSize: '12px', color: '#DDD6CF', marginTop: '4px', lineHeight: '1.5' }}>We do not sell personal data, build advertising profiles, or monetize your habits.</div>
            </div>

            <div style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.12)' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#98C1B3' }}>🔒 You Hold the Keys</div>
              <div style={{ fontSize: '12px', color: '#DDD6CF', marginTop: '4px', lineHeight: '1.5' }}>You decide if and when to share progress with a therapist, and you can disconnect anytime with 1 tap.</div>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: '#1A1615', color: '#B3AAA3', padding: '48px 24px', textAlign: 'center', fontSize: '13px', borderTop: '1px solid #2D2826' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <LuminousBreakLogo size={40} showText={true} theme="dark" subtitle="Mindful browsing, one pause at a time." />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={() => onNavigateRoute ? onNavigateRoute('privacy') : setLegalModalOpen('privacy')}
              style={{ background: 'none', border: 'none', color: '#DDD6CF', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Privacy Policy
            </button>
            <button
              onClick={() => onNavigateRoute ? onNavigateRoute('terms') : setLegalModalOpen('terms')}
              style={{ background: 'none', border: 'none', color: '#DDD6CF', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Terms of Service
            </button>
            <button
              onClick={() => onNavigateRoute ? onNavigateRoute('support') : (window.location.href = '/support')}
              style={{ background: 'none', border: 'none', color: '#DDD6CF', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Support & Contact
            </button>
            <button
              onClick={onOpenPortal}
              style={{ background: 'none', border: 'none', color: '#DDD6CF', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Clinician Portal
            </button>
          </div>
          {/* General Wellness & Emergency Health Disclaimer */}
          <div style={{
            maxWidth: '820px',
            margin: '0 auto 24px',
            padding: '16px 20px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: '11.5px',
            lineHeight: '1.6',
            color: '#A89F98',
            textAlign: 'center',
          }}>
            <strong style={{ color: '#E5DCD3', display: 'block', marginBottom: '4px' }}>
              ⚠️ GENERAL WELLNESS NOTICE & CRISIS DISCLAIMER — NOT MEDICAL CARE
            </strong>
            Luminous Break is a non-clinical behavioral habit awareness and mindfulness software tool. It does not provide medical or psychological diagnosis, psychotherapy, psychiatric treatment, or crisis intervention services, and is not a medical device. If you are experiencing a mental health emergency, thoughts of self-harm, or severe medical crisis, immediately call or text <strong>988</strong> (Suicide & Crisis Lifeline) or call <strong>911</strong> (US/Canada), <strong>111/999</strong> (UK), or <strong>112</strong> (EU).
          </div>

          <p style={{ margin: 0, fontSize: '11px', color: '#B3AAA3' }}>
            © 2026 Luminous Break Inc. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal
        isOpen={legalModalOpen === 'privacy'}
        onClose={() => setLegalModalOpen(null)}
        title="Privacy Policy"
      >
        <PrivacyPolicyContent />
      </LegalModal>

      {/* Terms of Service Modal */}
      <LegalModal
        isOpen={legalModalOpen === 'terms'}
        onClose={() => setLegalModalOpen(null)}
        title="Terms of Service"
      >
        <TermsOfServiceContent />
      </LegalModal>

      {/* Stripe Mock Checkout Modal */}
      <StripeMockCheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        plan={checkoutPlan}
        onSuccess={(receipt) => {
          alert(`✓ Stripe Mock Checkout Succeeded!\n\nPlan: ${receipt.planName}\nSimulated Invoice ID: ${receipt.id}\nAmount: ${receipt.amount}\nYour 14-Day Free Trial is active! Download the mobile app to sync.`);
        }}
      />

      {/* Stripe Customer Portal Self-Service Modal */}
      <StripeCustomerPortalModal
        isOpen={isCustomerPortalOpen}
        onClose={() => setIsCustomerPortalOpen(false)}
      />
    </div>
  );
};

export default MarketingLandingPage;
